"use client";

/* ===== easy-tedu · CORE control / mirror screen ===== */
import { captionLocale } from "@/lib/caption";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { YouTubeMirror } from "./youtube-mirror";
import { setMirrorRate } from "./mirror-rate";
import { logMirror } from "./mirror-telemetry";
import { I } from "./icons";
import {
  AppBar, Poster, Segmented, CheerButton, ScrollSolidify, useSolidifyRoot,
  useData, computeNowPlaying, useGripReorder, GripHandle, useSegRefLabel } from "./ui";
import { useMirrorQuality } from "@/lib/mirror-quality";
import { fmt, thumbUrl, isYouTube, addedPlaySet, type DeviceView, type NowPlaying, type PlaylistView, type VideoView, type RecentCast } from "./types";
import { FormDrill, type MasterForm } from "./setlist-screen";
import type { MasterPlayVideo, DrillPlayOpt } from "@/lib/realtime-types";
import { parseSegment } from "@/lib/ref-segment";
import { diag, diagOn, bufferedAhead } from "@/lib/diag";
import { BASIC_PREFIX, isBasicName, poomsaeRank, showsBasics } from "@/lib/browse-meta";
import { tkdTitle, tkdPlaylist, tkdBasicRegion, tkdLabel } from "@/lib/tkd-terms";
import { useLocale, useTranslations } from "next-intl";
import { BrandLockup } from "@/components/brand-lockup";
import { isAppleDevice } from "@/lib/device-platform";
import { CheerOverlay } from "@/components/cheer-overlay";
import { CaptionOverlay, useVideoCaptions } from "@/components/caption-bar";
import { useCachedVideoSrc } from "@/lib/mirror-cache";
import { retryVideoOnAccessError } from "@/lib/video-access";
import { BLANK_POSTER } from "@/lib/video-poster"; // 안드로이드 회색 기본 포스터 막기

export type UICmd = (action: string, arg?: number | string | string[]) => void;

/* seekable progress bar (loop: A-B 구간 % 표시) */
function SeekBar({ value, color = "var(--accent)", h = 6, onSeek, onScrub, knob, loop }: {
  value: number; color?: string; h?: number; onSeek?: (r: number) => void; knob?: boolean; loop?: { a: number; b: number };
  // 주면 '끄는 동안은 화면만(onScrub) · 손 뗄 때 한 번 onSeek'. 재생 위치용.
  // 🛑 끄는 동안 0.1초마다 SEEK 를 보내면 스탠바이미처럼 TV 재생기를 쓰는 기기는 이동마다 영상 조각을 새로 받다
  //    버리기를 되풀이해, 끄는 내내 멈추고 손 뗀 뒤에도 밀린 이동을 치우느라 한참 멈췄다(2026-10-15 신고).
  onScrub?: (r: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [seeking, setSeeking] = useState(false);
  const ratio = (clientX: number) => {
    const el = ref.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  };
  const seek = (clientX: number) => {
    const r = ratio(clientX);
    if (r != null) onSeek?.(r);
  };
  const down = (e: React.PointerEvent) => {
    setSeeking(true);
    if (onScrub) {
      let lastX = e.clientX;
      const show = (x: number) => { const r = ratio(x); if (r != null) onScrub(r); };
      show(lastX);
      const mv = (ev: PointerEvent) => { lastX = ev.clientX; show(lastX); };
      const up = () => {
        seek(lastX); // 손 뗀 자리로 한 번만
        setSeeking(false);
        window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up);
      };
      window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", up);
      return;
    }
    seek(e.clientX);
    // 드래그 중 명령은 ~100ms 로 throttle (포인터 이동마다 쏘면 SET_VOLUME/SEEK 가 수백 개 폭주 →
    // 좁은 회선에서 미러·잠금 등 다른 명령을 밀어냄). 손 뗄 때 최종값은 반드시 1회 전송.
    let last = 0; let pendingX: number | null = null;
    const mv = (ev: PointerEvent) => {
      pendingX = ev.clientX;
      const now = performance.now();
      if (now - last >= 100) { last = now; seek(pendingX); pendingX = null; }
    };
    const up = () => {
      if (pendingX != null) seek(pendingX); // 최종값 보장
      setSeeking(false);
      window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up);
  };
  return (
    <div ref={ref} onPointerDown={onSeek ? down : undefined} style={{ position: "relative", padding: "8px 0", cursor: onSeek ? "pointer" : "default", touchAction: "none" }}>
      <div className="track" style={{ height: h }}>
        <div className="track__fill" style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: color, transition: "width .15s linear" }} />
        {loop && (
          <div style={{ position: "absolute", top: 0, bottom: 0, left: `${loop.a}%`, width: `${Math.max(0, loop.b - loop.a)}%`, background: "rgba(255,213,0,.5)" }} />
        )}
      </div>
      {/* 노브 — 깔끔한 흰 원 + 옅은 그림자/헤어라인(예전 3px accent 링=촌티라 제거) */}
      {knob && <div style={{
        position: "absolute", top: "50%", left: `${Math.min(100, Math.max(0, value))}%`, width: 16, height: 16, borderRadius: "50%",
        background: "#fff", boxShadow: "0 2px 6px rgba(15,23,42,.28), 0 0 0 1px rgba(15,23,42,.06)",
        transform: `translate(-50%,-50%) scale(${seeking ? 1.25 : 1})`, transition: "left .15s linear, transform .12s ease",
      }} />}
    </div>
  );
}

/* transport controls */
function TransportBtn({ children, onClick, primary, ariaLabel, disabled, big, tour }: {
  children: React.ReactNode; onClick: () => void; primary?: boolean; ariaLabel: string; disabled: boolean; big?: boolean;
  /** 안내·자동 시연이 잡을 표식(data-tour). 감싸는 상자를 두면 자리가 0 이 돼 안내가 못 찾는다. */
  tour?: string;
}) {
  return (
    <button data-tour={tour} aria-label={ariaLabel} disabled={disabled} onClick={onClick} className={primary ? "btn ctl-play" : "btn"} style={{
      width: primary ? (big ? 72 : 62) : (big ? 52 : 48),
      height: primary ? (big ? 72 : 62) : (big ? 52 : 48),
      flex: "none", // 좁은 칸에서 재생 버튼만은 절대 안 찌그러지게(줄이는 건 간격·라벨 글리프 몫)
      borderRadius: "50%",
      background: primary ? "linear-gradient(180deg,#4a68d0,var(--accent))" : "var(--surface-2)",
      color: primary ? "#fff" : "var(--text-1)",
      border: primary ? "none" : "1px solid var(--border)",
      opacity: disabled ? 0.4 : 1,
      boxShadow: primary ? "var(--shadow-accent)" : "none",
    }}>{children}</button>
  );
}

/* v10 — 이전/다음은 라벨 달린 글리프(원형 아님), 재생만 큰 원형 accent (관장님도 뭔지 바로 인지) */
function TransportGlyph({ children, label, ariaLabel, disabled, onClick }: {
  children: React.ReactNode; label: string; ariaLabel: string; disabled: boolean; onClick: () => void;
}) {
  return (
    <button className="btn" aria-label={ariaLabel} disabled={disabled} onClick={onClick} style={{
      background: "none", border: "none", cursor: "pointer", opacity: disabled ? 0.4 : 1,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 3, color: "var(--text-1)",
      // 기본 56px 이되 칸이 좁으면 40까지 양보(넘쳐서 잘리는 것보다 조금 좁아지는 게 낫다)
      flex: "0 1 56px", minWidth: 40,
    }}>
      {children}
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)" }}>{label}</span>
    </button>
  );
}
function Transport({ device, onCmd, big }: { device: DeviceView; onCmd: UICmd; big?: boolean }) {
  const t = useTranslations("control.screen");
  const disabled = !device.online;
  const playing = device.status === "playing";
  return (
    // 간격은 CSS(.ctl-transport)로 — 인라인이면 좁은 칸에서 시네마 조작줄이 못 줄여 잘린다
    <div className={`row ctl-transport${big ? " ctl-transport--big" : ""}`} style={{ justifyContent: "center", alignItems: "center" }}>
      <TransportGlyph ariaLabel={t("prev")} label={t("prev")} disabled={disabled} onClick={() => onCmd("PREV")}><I.prev size={22} /></TransportGlyph>
      {/* tour="ctl-play" — 자동 시연 코스①이 '멈췄다 다시 트는' 자리로 누른다. */}
      <TransportBtn primary tour="ctl-play" disabled={disabled} big={big} ariaLabel={playing ? t("pause") : t("play")} onClick={() => onCmd(playing ? "PAUSE" : "PLAY")}>
        {playing ? <I.pause size={big ? 30 : 27} /> : <I.play size={big ? 30 : 27} style={{ marginLeft: 2 }} />}
      </TransportBtn>
      <TransportGlyph ariaLabel={t("next")} label={t("next")} disabled={disabled} onClick={() => onCmd("NEXT")}><I.next size={22} /></TransportGlyph>
    </div>
  );
}

/* 볼륨 슬라이더 — 폰·태블릿 공통 인라인(가로). 트랙/노브는 SeekBar 재사용으로 재생바와 통일. */
function VolumeSlider({ device, onCmd }: { device: DeviceView; onCmd: UICmd }) {
  const t = useTranslations("control.screen");
  // 낙관값(0..1) — 드래그/버튼 직후 잠깐 이 값을 보여준다. 없으면 기기 보고값을 따른다.
  // 유튜브 재생 중엔 기기가 300ms마다 상태를 보고하는데(onYtTick), 이 보고가 내 드래그가 왕복하기 전의
  // 옛 볼륨을 실어 오면 노브를 도로 100으로 홱 끌어당긴다("줄이면 100으로 되돌아감"). 그래서 드래그 중엔
  // 보고를 무시하고 내가 겨눈 값을 붙잡아 두었다가, 보고가 그 값에 도달하면(또는 안전시간 경과) 손을 뗀다.
  const [pending, setPending] = useState<number | null>(null);
  const holdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (holdRef.current) clearTimeout(holdRef.current); }, []);
  const send = (v: number) => {
    const clamped = Math.min(1, Math.max(0, v));
    setPending(clamped);
    // 마지막 조작 뒤 잠깐만 붙잡았다 놓는다. 기기는 내가 보낸 값을 그대로 되보고하므로(v.volume),
    // 이 창이 지나 놓아도 노브가 튀지 않는다. 창이 있어 왕복 지연·중간 하트비트에도 밀리지 않는다.
    if (holdRef.current) clearTimeout(holdRef.current);
    holdRef.current = setTimeout(() => setPending(null), 1200);
    onCmd("SET_VOLUME", +clamped.toFixed(2));
  };
  const shown = pending ?? device.volume; // 0..1
  const muted = shown === 0;
  // 아이폰/아이패드: iOS 는 JS 로 미디어 볼륨을 못 바꾼다(하드웨어 버튼 전용, video.volume 은 늘 1).
  // 슬라이더는 원리적으로 동작 못 하므로(늘 100 튕김) 숨기고, 되는 것(음소거)만 남긴다.
  if (device.volumeFixed || isAppleDevice(device.platform)) return <VolumeFixedControl device={device} onCmd={onCmd} />;
  return (
    <div data-tour="knob-vol" className="ctl-volslider">
      <button className="btn" aria-label={muted ? t("unmute") : t("mute")} disabled={!device.online}
        onClick={() => send(muted ? 0.6 : 0)}
        style={{ background: "none", border: "none", cursor: "pointer", flex: "none", width: 26, height: 26, padding: 0, display: "grid", placeItems: "center", color: muted ? "var(--text-3)" : "var(--text-1)" }}>
        {muted ? <I.mute size={20} /> : <I.volume size={20} />}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <SeekBar value={shown * 100} knob color="var(--accent)" onSeek={send} />
      </div>
      <span className="mono" style={{ flex: "none", width: 30, textAlign: "right", fontSize: 13, fontWeight: 600, color: "var(--text-2)" }}>{Math.round(shown * 100)}</span>
    </div>
  );
}

/* 아이폰/아이패드 전용 볼륨 컨트롤 — 슬라이더 대신 음소거 토글 + 안내.
   iOS 는 볼륨 조절이 하드웨어 버튼 전용이라 슬라이더가 안 먹힌다. 음소거(video.muted)만 원격으로 된다. */
function VolumeFixedControl({ device, onCmd }: { device: DeviceView; onCmd: UICmd }) {
  const t = useTranslations("control.screen");
  const [pendingMute, setPendingMute] = useState<boolean | null>(null); // 탭 직후 잠깐 낙관 표시
  const holdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (holdRef.current) clearTimeout(holdRef.current); }, []);
  const muted = pendingMute ?? device.muted;
  const toggle = () => {
    if (!device.online) return;
    const next = !muted;
    setPendingMute(next);
    if (holdRef.current) clearTimeout(holdRef.current);
    holdRef.current = setTimeout(() => setPendingMute(null), 1500);
    onCmd("SET_VOLUME", next ? 0 : 0.6); // 플레이어가 이 값으로 video.muted 를 토글(아이폰도 먹음)
  };
  return (
    <div data-tour="knob-vol" className="ctl-volslider">
      <button className="btn" aria-label={muted ? t("unmute") : t("mute")} disabled={!device.online} onClick={toggle}
        style={{ background: "none", border: "none", cursor: "pointer", flex: "none", width: 26, height: 26, padding: 0, display: "grid", placeItems: "center", color: muted ? "var(--danger)" : "var(--text-1)" }}>
        {muted ? <I.mute size={20} /> : <I.volume size={20} />}
      </button>
      <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 500, color: "var(--text-3)", lineHeight: 1.3 }}>
        {muted ? t("mutedPrefix") : ""}{t("volumeByDevice")}
      </span>
    </div>
  );
}

// 미러 리드 오프셋(초) — 미러를 디바이스보다 살짝 앞을 겨냥하게 해 네트워크 전달 지연(플레이어→서버→
// 컨트롤러, 주로 플레이어 업링크)을 상쇄한다. 보고에 그 지연이 안 들어와 미러가 늘 ~그만큼 뒤처지므로.
// 회선 지연은 변동이라 정확히 0 은 아님 — 미러가 디바이스보다 뒤처지면 키우고, 앞서가면 줄인다.
const MIRROR_LEAD = 0.3;
const SEEK_COOLDOWN = 2000; // 보정 seek 후 재seek 금지 시간(ms) — 디코더 재버퍼가 끝나 재생이 실제로 시작될 여유
// 재생바를 당긴 뒤 '내가 겨눈 자리'를 붙잡아 두는 시간(ms)과, 기기가 거기 닿았다고 볼 오차(초).
// 🔑 유튜브 seek 는 재버퍼라 mp4 보다 한참 느리다 — 1초짜리 창으로는 되돌아감을 못 막는다.
//    닿으면 그 자리에서 놓으므로(위 seekPos 주석) 이 값은 '최악의 경우 이만큼만 기다린다'는 뜻이다.
const SEEK_HOLD_MS = 4000;
const SEEK_LAND_TOL = 1.5;
// 보고 끊김 안전장치 — status="playing" 인데 이 시간 넘게 새 보고(ts 갱신)가 없으면 플레이어가 실제로 멈춘
// 것으로 보고 미러도 정지. 플레이어가 hidden 을 못 보낸 경우(옛 빌드·하드프리즈)까지 커버(하트비트 500ms 라 2s=4틱 누락).
const MIRROR_STALE_MS = 2000;
// 미러 안전장치(자가조정) — 회선이 감당 못 하면 썸네일로 폴백해 회선 해방. 횟수 대신 "진행 정지 시간"으로 판단.
const STALL_MS = 3500;      // 막힘 후 이 시간 내 재생 진행 없으면 썸네일 폴백
// 이벤트(waiting/stalled)가 아예 안 뜨는 경우의 최후 감시 — 틀려는 의도가 있는데 이 시간 동안
// 실제 진행이 0이면 막힌 것으로 본다. 첫 프레임 전에는 영상이 투명이라(썸네일 노출) 사용자는
// '첫 장면에서 멈춘 화면'만 보고 이유를 알 수 없었다(실측 신고).
const NO_PROGRESS_MS = 7000;
// 미러 로직 리비전 — 진단 로그에 찍어 '이 로그가 어느 빌드에서 나왔는지'를 바로 안다.
// 브라우저가 옛 JS 를 캐시한 채 재현하면 고친 것도 안 고쳐진 것처럼 보인다(실측으로 겪음).
// ⚠️ 미러 관련 로직을 고칠 때마다 이 값을 바꿀 것.
const MIRROR_REV = "r6-rate-throttle";
const RETRY_BASE = 4000;    // 폴백 후 재시도 기본 지연(지수 백오프 시작값)
const RETRY_MAX = 30000;    // 재시도 지연 상한
const RETRY_JITTER = 3000;  // 재시도 지터 — 동시 복귀(재포화) 방지

/* live mirror surface — 디바이스가 재생 중인 실제 영상을, 보고된 위치에 맞춰 음소거 미리보기 */
export function Mirror({ np, device, position, fill, rounded = 16, captions = false, captionLang, onToggle }: {
  np: NowPlaying | null; device: DeviceView; position: number; fill?: boolean; rounded?: number; captions?: boolean; captionLang?: string; onToggle?: () => void;
}) {
  const t = useTranslations("control.screen");
  const locale = useLocale();
  const vref = useRef<HTMLVideoElement>(null);
  const holdLogRef = useRef(0); // '보정 보류' 진단 로그 스로틀
  const progressAtRef = useRef(0); // 마지막으로 '실제 진행'을 본 시각(timeupdate/playing) — 무진행 감시용
  // 미러가 기기보다 앞서 기기를 기다리며 멈춰 선 중 — 그동안은 재생 복구·onCanPlay 가 다시 틀지 않는다(아래 위치 싱크).
  const waitRef = useRef(false);
  // ⚠️ device.ts(기기 보고 시각)는 재생 중 500ms 마다 바뀐다. 이걸 아래 이펙트 의존성에 넣으면
  //    이펙트가 0.5초마다 재생성되며 타이머가 매번 리셋돼, 800ms 복구 루프도 3.5초 막힘 감시도
  //    한 번도 발화하지 못한다(실측: 미러가 pause 된 뒤 영영 안 살아나고 안내도 안 떴다).
  //    값은 최신이 필요하되 이펙트를 다시 만들면 안 되므로 ref 로 읽는다.
  const deviceTsRef = useRef(device.rxAt); // 신선도 판정은 '받은 로컬 시각'으로 — 기기 시계에 기대지 않는다
  deviceTsRef.current = device.rxAt;
  const seekAtRef = useRef(0);  // 마지막 보정 seek 시각 — seek 직후 waiting 을 기아로 오탐하지 않게
  const goodBwRef = useRef(false); // 미러본이 통째로 버퍼된 적 있음(회선 양호) — 버퍼 밖 즉시 seek 허용 판단
  const vidChangeRef = useRef(0); // 영상이 막 바뀐 시각 — 직후엔 옛 영상의 position 으로 잘못 점프하지 않게 보정 보류
  const loadedSrcRef = useRef(""); // 현재 video 에 실제로 물린 src — 같은 영상 중복 재로드(=t0 리셋·재fetch) 방지
  const prevPosRef = useRef(0); // 직전 보고 위치 — 루프 중 '앞으로 직접 seek' 감지(원형 보정 우회)용
  const playing = device.status === "playing";

  // 저용량 미러 변환본만 재생 — 존재 여부는 서버가 영상 목록에 동봉(VideoView.mirror).
  // 클라이언트 HEAD 확인은 모바일에서 왕복 수 초가 걸려 미러 초반이 비는 원인이었음(MOBILE_DIAG).
  // 원본(폰 4K/HEVC 등)으로 폴백하면 제어 기기가 디코딩/스트리밍을 못 따라가 멈춤·회선 포화를 유발했다
  // (큰 고화질 업로드 직후 증상). 미러본이 아직 없으면 썸네일+"준비 중"만 보이고, 미러 완성 시점에
  // 목록이 재조회되며(page.tsx hls-events 'mirror') 자동으로 가벼운 미러로 전환된다.
  // 제어화면 상세 — 회선을 타는 소스는 360p(mirror) 하나뿐. 없을 때만 480p(mirror-hi) 폴백.
  // 480p 상세본을 회선으로 당기면 타일 여러 개와 같은 혼잡 와이파이를 동시에 물어 미러·타일이 함께
  // 굶었다(실측 MOBILE_DIAG: 상세[mirror] + 타일 3개 경합 → 깊은 위치 타일이 가장 늦게 뜸).
  // 🛑 이 순서를 480p 우선으로 되돌리지 말 것 — 화질보다 회선 여유가 우선이다.
  //
  // ⭐ 화질은 화면 크기로 가른다(lib/mirror-quality.ts) — 폰은 예전 그대로 360p, 태블릿·PC 는 480p.
  //    · hiStream(PC 급, CSS 폭 900 초과)일 때만 회선으로 480p 를 당긴다. 폰·태블릿은 그대로 360p —
  //      위 경합 실측이 바로 그 기기들에서 나온 것이라 건드리지 않는다.
  //    · hiCache(미러 실제 픽셀 1200 초과)면 480p 를 '담아뒀다가' 다음 재생부터 쓴다. 담기는 재생이
  //      끝난 유휴 시간이라 회선 경합과 무관하다 → 태블릿도 첫 재생만 360p 고 그다음부터 480p.
  // 유튜브 소스는 로컬 미러본이 없음(파일 접근 불가) → 라이브 미러 대신 썸네일+타임라인 상태뷰(옵션 가).
  const isYt = isYouTube(np?.vid);
  const mq = useMirrorQuality();
  // 동작 자막(도장 설정) — 기기와 같은 조각을 미러 시각으로 그린다(components/caption-bar.tsx)
  const captionCues = useVideoCaptions(captions && !isYt ? np?.vid?.id : null, captions && !isYt);
  const hiRel = isYt ? null : (np?.vid?.mirrorHi ?? null);
  const hiTag = isYt ? null : (np?.vid?.mirrorHiTag ?? null);
  const loRel = isYt ? null : (np?.vid?.mirror ?? null);
  const streamHi = mq.hiStream && !!hiRel; // PC 급 화면만
  const mirrorRel = streamHi ? hiRel : (loRel ?? hiRel);
  const mirrorTag = streamHi ? hiTag : (loRel ? (np?.vid?.mirrorTag ?? null) : hiTag);
  // 담아뒀다가 다음 재생부터 쓸 고화질. 이미 480p 를 흘려보내는 중이면 그게 그대로 담기므로 불필요.
  const upgrade = !streamHi && mq.hiCache && hiRel && loRel ? { rel: hiRel, tag: hiTag } : undefined;
  const mirrorPending = !isYt && !!np?.vid && !mirrorRel; // 미러본 준비 전 — 썸네일 위에 '준비 중' 표시(유튜브는 미러 없음이 정상이라 제외)

  // 저속 자동 전환 — 회선이 비트레이트를 못 따라가면(기아 반복) 미러 스트리밍을 멈추고
  // 썸네일만 표시. 다운로드를 끊어 회선을 비워주므로 잠금/명령 등 다른 요청도 빨라진다.
  // (모바일 실측 7.4Mbps 영상 vs 1~3Mbps 회선 — MOBILE_DIAG 분석 기록 참고)
  const [degraded, setDegraded] = useState(false);
  // 영상이 바뀌어 src 를 다시 로드하는 동안 video 가 이전 영상의 마지막 프레임을 그대로 붙들어
  // "멈춘 채로 있다가 새 영상이 뜨는" 증상(특히 자유 재생 중 영상이 자주 바뀔 때)을 막는다.
  // 첫 프레임 디코드 전까지 video 를 숨겨 그 아래 썸네일(Poster)을 노출.
  const [hasFrame, setHasFrame] = useState(false);
  // 로컬 캐시 우선 — 전에 한 번 본 미러본이면 blob 으로 즉시(네트워크 0·seek 즉시), 없으면 네트워크로
  // 재생하고 회선이 한가할 때 받아둔다(lib/mirror-cache.ts). 재생 중 소스를 바꾸지 않으므로
  // 아래 loadedSrcRef 가드·드리프트 보정 로직은 그대로다.
  const src = useCachedVideoSrc(mirrorRel, mirrorTag, { streaming: playing && !degraded, upgrade });
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // 막힘 → 썸네일 폴백 타임아웃
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);  // 썸네일 → 재시도 백오프
  const failCountRef = useRef(0); // 같은 영상 연속 폴백 횟수(백오프 지수). 새 영상이면 0

  // 영상 바뀌거나 배속 변경 시 폴백·백오프 리셋 — 썸네일에 갇히는 것 방지(새 영상은 깨끗한 기회). 타이머 정리.
  useEffect(() => {
    setDegraded(false);
    failCountRef.current = 0;
    progressAtRef.current = Date.now(); // 새 영상 — 무진행 기준도 새로(옛 기준으로 즉시 오판 방지)
    goodBwRef.current = false;
    if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
  }, [src, device.rate]);
  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    if (degraded) { loadedSrcRef.current = ""; setHasFrame(false); v.removeAttribute("src"); v.load(); return; } // 다운로드 중단 — 회선 해방
    // src 가 한 프레임 빈 값이 되는 경우(목록 재조회로 byId 교체 시 np.vid.mirror 일시 누락·np 깜빡임)엔
    // 물려둔 영상을 그대로 둔다. 여기서 loadedSrcRef 를 비우면 src 가 같은 값으로 복귀할 때 아래 가드가
    // 뚫려 동일 미러를 통째로 재로드(재버퍼·t0 리셋·썸네일 깜빡)했다(실측 더블 src 교체). 진짜 재생목록
    // 해제(playlistId=null)는 부모가 IdleBrowse 로 Mirror 를 언마운트해 자연 정리되므로 여기서 비울 필요 없다.
    if (!src) return;
    // 가드에 v.src 동반 — np 가 오래 null 이라 <video> 가 언마운트됐다 재마운트되면 새 요소엔 src 가 없는데
    // ref 만 남아 "이미 물림"으로 오판해 빈 화면이 된다. 요소가 비어있으면(v.src falsy) 재로드한다.
    if (loadedSrcRef.current === src && v.src) return; // 같은 영상 — 재로드 생략(중복 fetch·t0 리셋 방지). 위치는 드리프트 보정이 맞춤
    loadedSrcRef.current = src;
    setHasFrame(false); // 새 소스 → 첫 프레임 전까지 이전 프레임 대신 썸네일 노출
    diag("mirror", `src 교체 → ${src.slice(-40)}`);
    vidChangeRef.current = performance.now(); // 영상 바뀜 — 직후 보정 보류(아래 드리프트 effect)
    // 0부터 로드 — #t= 로 보고 위치를 시드했더니, 영상이 막 바뀐 순간 position 이 아직 옛(긴) 영상 값
    // (예 86·100초)이라 새(짧은) 영상을 그 위치로 잘못 열어 "완전히 다른 장면/영상"이 떴다(로그 실측 53~55분).
    // 0부터 켜고 신선한 보고가 온 뒤 드리프트로 따라잡는다(상세 미러는 크게 보여 오시작이 도드라지므로 0 시작).
    v.src = src;
    v.load();
  }, [src, degraded]);
  // 기록 — 틀어야 하는데 영상을 연 지 5초가 지나도 첫 화면조차 못 그리면 한 번 남긴다(영상마다 한 번).
  const noFrameLoggedRef = useRef("");
  useEffect(() => {
    if (!src || hasFrame || degraded || !playing || !device.online || device.hidden) return;
    if (noFrameLoggedRef.current === src) return;
    const started = performance.now();
    const tm = setTimeout(() => {
      noFrameLoggedRef.current = src;
      logMirror("mirror_no_frame", vref.current, undefined, performance.now() - started);
    }, 5000);
    return () => clearTimeout(tm);
  }, [src, hasFrame, degraded, playing, device.online, device.hidden]);
  // 표가 없어 영상이 안 열리면(403) 다시 받아 같은 영상을 한 번 더 건다(lib/video-access.ts).
  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    return retryVideoOnAccessError(v);
  }, [src]);
  // 안전장치(폴백) — 막힘(waiting/stalled/loadstart) 후 STALL_MS 안에 진행(playing/timeupdate)이 안 오면
  // "이 회선이 못 감당"으로 보고 썸네일 폴백(다운로드 끊어 회선 해방). 횟수 대신 정지 시간으로 판단.
  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    const clearStall = () => { if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; } };
    const onStall = () => {
      // 최소화(hidden)·보고끊김(stale)이면 미러를 '일부러' 멈춘 것 — 로드 지연을 '느린 회선'으로 오인해
      // 저속폴백(degraded, "재생은 정상이에요" 오버레이) 을 띄우면 안 된다(플레이어 최소화 상태의 오표시).
      if (!playing || degraded || stallTimerRef.current || device.hidden || Date.now() - deviceTsRef.current > MIRROR_STALE_MS) return;
      stallTimerRef.current = setTimeout(() => {
        stallTimerRef.current = null;
        diag("mirror", `로드 지연 ${STALL_MS}ms 초과 → 썸네일 폴백·다운로드 해방`);
        logMirror("mirror_fallback", vref.current, { why: "stall" });
        setDegraded(true);
      }, STALL_MS);
    };
    const onProgress = () => { progressAtRef.current = Date.now(); clearStall(); }; // 진행 재개 — 폴백 보류 해제
    v.addEventListener("waiting", onStall);
    v.addEventListener("stalled", onStall);
    v.addEventListener("loadstart", onStall);
    v.addEventListener("playing", onProgress);
    v.addEventListener("timeupdate", onProgress);
    return () => {
      clearStall();
      v.removeEventListener("waiting", onStall);
      v.removeEventListener("stalled", onStall);
      v.removeEventListener("loadstart", onStall);
      v.removeEventListener("playing", onProgress);
      v.removeEventListener("timeupdate", onProgress);
    };
  // src 를 의존성에 둔다 — 영상이 바뀌거나 <video> 가 재마운트되면 리스너가 옛 요소에 남아
  // 진행(timeupdate) 신호를 못 받고, 잘 돌고 있는데도 '무진행'으로 오판한다(실측 로그: 진행 중
  // paused=false ready=4 인데 폴백 발동).
  }, [playing, degraded, device.hidden, src]);
  // 다시 재생이 시작되면(정지→재생) 폴백·백오프를 즉시 푼다. 백오프는 최대 30초까지 늘어나는데,
  // 그동안은 썸네일에 갇혀 '다시 눌렀는데도 멈춰 있다'로 보인다. 사용자가 다시 튼 것은
  // '새 기회를 달라'는 뜻이므로 수동 재시도와 똑같이 취급한다.
  const lastSnapRef = useRef(0); // 진단 스냅샷 스로틀
  const wasPlayingRef = useRef(false);
  useEffect(() => {
    if (playing && !wasPlayingRef.current) { failCountRef.current = 0; setDegraded(false); }
    wasPlayingRef.current = playing;
  }, [playing]);
  // 최소화(hidden) 진입 시 저속폴백(degraded) 오버레이 즉시 해제 — 미러는 일부러 멈춘 것이라 '느린 회선' 안내가 부적절.
  useEffect(() => { if (device.hidden) setDegraded(false); }, [device.hidden]);
  // 폴백 후 백오프 재시도 — 지수 증가 + 지터로 동시 복귀(재포화) 분산. 영상 전환 시 위 리셋이 failCount 0 으로.
  useEffect(() => {
    if (!degraded) return;
    const n = failCountRef.current;
    failCountRef.current = n + 1;
    const delay = Math.min(RETRY_BASE * Math.pow(2, n), RETRY_MAX) + Math.random() * RETRY_JITTER;
    retryTimerRef.current = setTimeout(() => { retryTimerRef.current = null; setDegraded(false); }, delay);
    return () => { if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; } };
  }, [degraded]);
  // 진단(?diag=1) — 버퍼링/정지 이벤트 추적. waiting·stalled 가 반복되면 네트워크 처리량 부족,
  // 버퍼여유가 충분한데 멈추면 디코딩/탭 스로틀링 쪽. docs/MOBILE_DIAG.md 해석 가이드 참고.
  useEffect(() => {
    if (!diagOn()) return;
    const v = vref.current;
    if (!v) return;
    const log = (e: Event) =>
      diag("mirror", `${e.type} t=${v.currentTime.toFixed(1)} 버퍼여유=${bufferedAhead(v)}s ready=${v.readyState} net=${v.networkState}${v.error ? ` err=${v.error.code}` : ""}`);
    const evs = ["loadstart", "loadedmetadata", "canplay", "playing", "waiting", "stalled", "suspend", "pause", "error"];
    evs.forEach((t) => v.addEventListener(t, log));
    return () => evs.forEach((t) => v.removeEventListener(t, log));
  }, []);
  // 첫 프레임 준비되면 video 노출 (그 전까진 opacity:0 으로 썸네일 폴백 — 영상 전환 시 이전 프레임 잔상 방지)
  // ⚠️ src 를 의존성에 둔다. 예전엔 마운트 때 한 번만 붙여서, <video> 가 재마운트되면 리스너가 옛 요소에
  //    남아 hasFrame 이 영영 false → 영상은 정상 재생되는데 opacity:0 으로 숨겨져 썸네일만 보였다
  //    (실측 로그: paused=false ready=4 t=2.1 인데 화면은 '첫 장면에서 멈춤'). 그리드 타일도 같은 버그였다.
  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    const reveal = () => setHasFrame(true);
    // 이미 프레임이 있으면(재부착 시점에 이미 디코드 완료) 이벤트를 기다리지 않고 바로 노출.
    if (v.readyState >= 2) setHasFrame(true);
    v.addEventListener("loadeddata", reveal); // readyState>=2 = 첫 프레임 디코드 완료
    v.addEventListener("playing", reveal);
    return () => { v.removeEventListener("loadeddata", reveal); v.removeEventListener("playing", reveal); };
  }, [src]);
  // 재생/일시정지 미러링 (음소거 자동재생 허용).
  // device.hidden(플레이어 탭 최소화)이면 실제 재생이 멈춰 있으므로 미러도 멈춘다 — 안 그러면 컨트롤러 미러만
  // 마지막 "playing" 잔상으로 계속 흘러 "혼자 움직이는" 증상. 복귀(hidden=false) 시 위치싱크가 재동기.
  useEffect(() => {
    const v = vref.current;
    if (!v || degraded) return;
    if (!playing || !device.online || device.hidden) waitRef.current = false;
    if (playing && device.online && !device.hidden) { if (!waitRef.current) v.play().catch(() => {}); }
    else v.pause();
  }, [playing, device.online, degraded, device.hidden]);
  // 재생 복구 — play() 가 거부되거나(그리드→단일 전환 시 디코더 경합 등) 멈춰 ready 상태로 방치되면
  // onCanPlay 가 다시 안 떠 미러가 영영 멈춘다. 재생 의도가 있는데 멈춰 있으면 ~1초마다 재시도.
  useEffect(() => {
    if (!playing || !device.online || degraded || device.hidden) return;
    progressAtRef.current = Date.now(); // 기준 초기화 — 옛 시각이 남아 즉시 오탐하는 걸 막는다
    const iv = setInterval(() => {
      const v = vref.current;
      if (!v) return;
      // 보고 끊김(플레이어 최소화·프리즈인데 hidden 미수신) → 미러도 정지. 보고 재개(device.ts 갱신) 시
      // effect 가 재생성돼 다시 아래 복구 로직으로 돌아온다.
      if (Date.now() - deviceTsRef.current > MIRROR_STALE_MS) { if (!v.paused) v.pause(); return; }
      if (waitRef.current) { progressAtRef.current = Date.now(); return; } // 일부러 기다리는 중 — 막힘이 아니다
      if (v.paused && !v.error) v.play().catch((e) => diag("mirror", `play() 거부 — ${String(e).slice(0, 60)}`));
      // 왜 안 살아나는지 다음 재현에서 바로 갈리도록 상태를 남긴다(?diag=1 세션에서만·2초 간격).
      if (diagOn() && Date.now() - lastSnapRef.current > 2000) {
        lastSnapRef.current = Date.now();
        diag("mirror", `상태[${MIRROR_REV}] status=${device.status} playing=${playing} degraded=${degraded} paused=${v.paused} ready=${v.readyState} net=${v.networkState} t=${v.currentTime.toFixed(1)} src=${v.currentSrc ? "있음" : "없음"}`);
      }
      // 최후 감시 — play() 를 계속 부르는데도 진행이 없으면(첫 프레임조차 못 만든 경우 포함)
      // 이벤트가 안 떠도 막힌 것으로 보고 안내 화면으로 넘긴다. 그래야 사용자가 이유를 알고,
      // 백오프 재시도와 회선 해방(다운로드 중단)도 함께 걸린다.
      if (progressAtRef.current && Date.now() - progressAtRef.current > NO_PROGRESS_MS) {
        diag("mirror", `무진행 ${NO_PROGRESS_MS}ms — 이벤트 없이 막힘 → 썸네일 폴백`);
        logMirror("mirror_fallback", v, { why: "no_progress" });
        setDegraded(true);
      }
    }, 800);
    return () => clearInterval(iv);
  }, [playing, device.online, degraded, device.hidden]);
  // A-B 구간반복 경계 (없으면 null) — 아래 자율 루프 + 위치싱크 양쪽이 공유, 의존성도 원시값으로 안정화
  const loopA = device.loop?.a ?? null;
  const loopB = device.loop?.b ?? null;
  const hasLoop = loopA != null && loopB != null && loopB > loopA;
  // A-B 구간반복 — 미러도 디바이스와 똑같이 B 도달 즉시 A로 점프(자율 루프).
  // 이게 없으면 미러는 루프를 모른 채 B 너머로 계속 흐르고, 아래 위치싱크가 디바이스의 'B→A 되돌림'을
  // (짧은 구간일수록) '미러가 앞섬'으로 오인해 감속만 하다 한 바퀴 늦게야 따라잡았다(보고된 증상의 원인).
  // 위치싱크는 아래에서 루프 내 '원형 거리'로 위상만 미세보정하므로 이 자율 점프와 충돌하지 않는다.
  useEffect(() => {
    const v = vref.current;
    if (!v || !hasLoop || degraded) return;
    const onLoop = () => {
      // 막 점프(B→A)한 구간은 방금 재생해 버퍼에 있으므로 재버퍼 없이 매끈하게 되돌아간다.
      // 미러본(저용량 변환본)은 원본보다 길이가 약간 짧을 수 있어, B 가 원본 끝 부근이면 loopB 가
      // 미러 길이를 넘어 자율 점프가 영영 안 걸린다(미러가 제 끝에서 멈춤). 미러 자신의 길이에도 맞춰 클램프.
      const wrapAt = Math.min(loopB!, (v.duration || loopB!) - 0.1);
      if (v.currentTime >= wrapAt) {
        try { v.currentTime = Math.min(loopA!, wrapAt); } catch { /* not seekable yet */ }
      }
    };
    v.addEventListener("timeupdate", onLoop);
    return () => v.removeEventListener("timeupdate", onLoop);
  }, [hasLoop, loopA, loopB, degraded]);
  // 위치 싱크 — "끊어 맞추기(seek)" 대신 배속 미세조정으로 디바이스에 스르륵 수렴시켜 깜빡임을 없앤다.
  //  • 작은~중간 어긋남(≤2.5초): 배속을 어긋남 비례(±최대 50%)로 틀어 부드럽게 따라붙음(seek 없음).
  //    seek 는 매번 재버퍼(waiting)로 미러를 잠깐 멈춰 다시 뒤처지게 만들어 ±1초대 연쇄 seek 를 유발했음
  //    (실기기 실측) → 임계값을 올려 그 구간은 배속으로만 수렴시킨다.
  //  • 큰 어긋남(전환/원격 seek/디바이스 resume, >2.5초): 즉시 그 위치로 seek.
  //  • 단 버퍼 밖 seek 는 다운로드를 리셋해 저대역폭에서 폭주(과거 실측, MOBILE_DIAG). 그래서 회선이
  //    아직 미확인(미러본을 통째로 못 받아봄)일 때만 버퍼 밖을 보류하고 degrade 에 맡긴다. 회선이 좋으면
  //    (goodBwRef: 버퍼 10초+ 달성) 버퍼 밖이라도 즉시 점프 — 미러본은 Range 로 그 지점부터 금방 받는다.
  //  • base 배속(device.rate)도 이 effect 가 함께 적용 — 미러가 1x 고정이면 영영 못 따라잡음.
  useEffect(() => {
    const v = vref.current;
    if (!v || !np?.vid || degraded) return;
    // 플레이어 최소화(hidden) 중엔 실제 재생이 멈춰 있으므로 보정도 멈춘다 — 미러를 정지 상태로 두고
    // 복귀 시 신선한 보고로 재동기(안 그러면 멈춘 미러 위치를 옛 외삽 타깃으로 억지로 끌고 간다).
    if (device.hidden) { v.pause(); return; }
    const base = device.rate || 1;
    const stopWaiting = () => { if (!waitRef.current) return; waitRef.current = false; if (playing && device.online) v.play().catch(() => {}); };
    // 영상 전환 직후 ~1.2초는 보정 보류 — position 이 아직 옛 영상 값일 수 있어(레이스) 그 값으로 점프하면
    // "미러만 6초부터" 버그. 그 사이 미러는 0부터 재생하고, 신선한 보고가 온 뒤 정상 동기화.
    if (performance.now() - vidChangeRef.current < 1200) { stopWaiting(); setMirrorRate(v, base); return; }
    const dur = v.duration;
    const lead = position + MIRROR_LEAD; // 전달 지연 상쇄 — 미러가 디바이스보다 살짝 앞을 겨냥
    const target = dur ? Math.min(lead, dur - 0.1) : lead;
    let diff = target - v.currentTime; // +면 미러가 디바이스보다 뒤처짐
    // 루프 중 사용자가 시크바로 '앞으로 직접 seek' 하면 보고 위치가 정상 재생분을 넘어 앞으로 튄다.
    // 그땐 아래 원형 보정이 그 점프를 '작은 뒤로 위상차'로 둔갑시켜 미러가 스냅하지 않고 느리게 수렴한다.
    // 자율 B→A 되돌림은 항상 '뒤로' 점프라, forward 점프만으로 의도적 seek 를 안전히 구분해 보정을 건너뛴다.
    // (임계값은 하트비트 1틱 진행분보다 크게 — 배속 비례로 키워 고배속에서 오탐 방지)
    const fwdSeek = device.positionSec - prevPosRef.current > Math.max(2, (device.rate || 1) * 1.5);
    prevPosRef.current = device.positionSec;
    // A-B 구간반복 중 미러·디바이스가 모두 루프 구간 안에 있으면 위치차를 '원형 거리'로 본다.
    // 미러는 위 effect 로 B→A 자율 점프하므로, 디바이스의 되돌림을 '큰 뒤로 점프'로 오인해
    // 감속/되감기하던 것을 없애고 루프 한 바퀴 안의 위상차만 최소경로로 미세보정한다.
    if (hasLoop && !fwdSeek) {
      const L = loopB! - loopA!;
      const inLoop = (t: number) => t >= loopA! - 0.5 && t <= loopB! + 0.5;
      if (inLoop(target) && inLoop(v.currentTime)) diff -= L * Math.round(diff / L); // → (-L/2, L/2]
    }
    const gap = Math.abs(diff);
    let inBuffered = false;
    let ahead = 0; // 현재 위치 기준 받아둔 여유(초)
    try {
      for (let i = 0; i < v.buffered.length; i++) {
        const s = v.buffered.start(i), e = v.buffered.end(i);
        if (target >= s && target <= e) inBuffered = true;
        if (v.currentTime >= s && v.currentTime <= e) ahead = e - v.currentTime;
      }
    } catch { /* buffered 접근 불가 */ }
    if (ahead > 10) goodBwRef.current = true; // 통째 버퍼됨 = 회선 양호

    // 보정 임계값 비대칭: 뒤처짐(diff>0, 앞으로 seek)은 빨리 따라잡되, 앞섬(diff<0, 뒤로 seek)은
    // 화면이 되감기처럼 튀므로 큰 경우(전환/원격 seek 잔재)만. 그 사이엔 감속으로 디바이스가
    // 따라오게 해 연쇄 되감기를 없앤다. (실측 MOBILE_DIAG 35:26~35:28: 배속 2x 중 디바이스 버퍼링 시
    //  미러가 추월 → 뒤로 seek → 다시 추월 → 또 뒤로 seek 의 1.9초 간격 플래핑)
    const seekGate = diff > 0 ? 2.5 : 6;
    if (gap > seekGate) { // 큰 어긋남(전환/원격 seek) → seek
      // 직전 보정 seek 후 쿨다운 — 막 seek 한 미러는 디코더가 재버퍼(ready=1) 중이라, 여기서 또 seek 하면
      // 재생을 영영 시작 못 하고 위치만 3초씩 점프하는 연쇄(死스파이럴, 실측 MOBILE_DIAG 50:30~52:07,
      // 51:45~52:07). 쿨다운 동안엔 재생을 우선(배속만 base)해 디코더가 스스로 따라잡게 둔다.
      if (performance.now() - seekAtRef.current < SEEK_COOLDOWN) { setMirrorRate(v, base); return; }
      if (!inBuffered && !goodBwRef.current && gap < 10) { // 버퍼 밖 + 회선 미확인: 폭주 방지로 보류
        setMirrorRate(v, base);
        if (performance.now() - holdLogRef.current > 3000) {
          holdLogRef.current = performance.now();
          diag("mirror", `보정 보류 — 타깃 ${target.toFixed(1)} 버퍼 밖·회선 미확인 (어긋남 ${gap.toFixed(1)}s, 버퍼여유=${bufferedAhead(v)}s)`);
        }
        return;
      }
      diag("mirror", `보정 seek ${v.currentTime.toFixed(1)}→${target.toFixed(1)} (어긋남 ${diff.toFixed(1)}s, 버퍼=${inBuffered ? "안" : "밖"})`);
      seekAtRef.current = performance.now();
      try { v.currentTime = Math.max(0, target); } catch { /* not seekable yet */ }
      stopWaiting();
      setMirrorRate(v, base);
      return;
    }
    // 앞섬 → **멈춰 서서 기기를 기다린다**(되감기 seek 없이). 예전엔 최대 0.4배로 감속했는데, 재생바로 옮긴 직후
    // 기기가 그 자리를 받느라 몇 초 서 있는 동안 미러만 0.4배 슬로모션으로 10초 넘게 기어갔다(2026-10-16 운영 실측:
    // 차이가 0.5→5초로 오히려 벌어짐 · 신고 "재생바 당기면 갑자기 속도가 느려짐"). 미러는 기기 화면을 비추는 것이라
    // 기기가 서 있으면 같이 서 있는 게 맞다. 0.4초 넘게 앞서면 멈추고, 0.1초 안으로 따라오면 다시 1배로 튼다.
    if (diff < -0.4 || (waitRef.current && diff < -0.1)) {
      if (!waitRef.current) diag("mirror", `기기 기다림 — 미러가 ${(-diff).toFixed(1)}s 앞섬`);
      waitRef.current = true;
      if (!v.paused) v.pause();
      setMirrorRate(v, base);
      return;
    }
    stopWaiting();
    // 뒤처짐은 +최대 50% 배속으로 따라붙고, 살짝 앞섬(0.2~0.4초)은 0.9배로만 맞춘다(0.2초 이내면 정상 배속).
    if (gap > 0.2) setMirrorRate(v, diff > 0 ? base * (1 + Math.min(0.5, gap * 0.25)) : base * 0.9);
    else setMirrorRate(v, base);
  }, [position, np?.vid?.id, degraded, device.rate, hasLoop, loopA, loopB, device.positionSec, device.hidden]);

  if (!np) {
    return (
      <div style={{ width: "100%", height: fill ? "100%" : undefined, aspectRatio: fill ? undefined : "16 / 9", flexShrink: 0,
        borderRadius: rounded, background: "var(--surface-2)", display: "grid", placeItems: "center", border: "1px solid var(--border)" }}>
        <div className="col gap-8" style={{ alignItems: "center", color: "var(--text-3)" }}>
          <I.monitor size={34} /><span style={{ fontSize: 13 }}>{t("loadPlaylistEmpty")}</span>
        </div>
      </div>
    );
  }
  return (
    // 부양 그림자는 CSS(.ctl-mirror)로 — 세로(폰 .ctl-hero)는 강한 hero 리프트, 태블릿 시네마는 조인 그림자로 분기.
    // data-tour — 자동 시연이 '결과는 여기서 보세요'로 짚는 자리(배속·구간반복은 이 그림에서 보인다).
    <div data-tour="ctl-mirror" onClick={onToggle} className={fill ? "cap-host" : "ctl-mirror cap-host"} style={{ position: "relative", width: "100%", height: fill ? "100%" : undefined,
      aspectRatio: fill ? undefined : "16 / 9", flexShrink: 0,
      borderRadius: rounded, overflow: "hidden", cursor: "pointer", background: "#000",
      border: fill ? "none" : "1px solid var(--border)" }}>
      {/* 세로영상 blur 배경 — 양옆 검은 여백 대신 콘텐츠 기반 블러(유튜브/Apple TV st). 16:9 영상은 뒤에 가려 안 보임 → 디코드 안 늘림(썸네일). */}
      {thumbUrl(np.vid) && (
        <div aria-hidden style={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: `url(${thumbUrl(np.vid)})`, backgroundSize: "cover", backgroundPosition: "center",
          // blur 만이 아니라 brightness↓·saturation↑ 로 검은 여백보다 자연스럽고 영상과 어우러지게
          filter: "blur(34px) saturate(1.5) brightness(0.82)", transform: "scale(1.25)", opacity: 0.75,
        }} />
      )}
      {/* 썸네일은 영상 로드 전 배경 폴백 — 실제 기기와 동일하게 contain(세로영상 안 잘림, 양옆 검은 여백) */}
      <Poster video={np.vid} w="100%" h="100%" radius={0} showLabel={false} showDur={false} big fit="contain" />
      {isYt && np?.vid?.youtubeId ? (
        // 유튜브 — 음소거 IFrame 미러(video 는 언마운트되어 이전 영상 프레임 잔상 없음)
        <YouTubeMirror videoId={np.vid.youtubeId} position={position} playing={playing && device.online && !device.hidden} rate={device.rate || 1} />
      ) : (
        // WebKit에서 정지 뒤 지연된 play/playing이 도착할 수 있어 이벤트 시점에도 재생 의도를 확인한다.
        <video ref={vref} muted autoPlay={playing && device.online && !device.hidden && !degraded} playsInline preload="auto" poster={BLANK_POSTER}
          onPlay={(event) => {
            if (!playing || !device.online || device.hidden || degraded || Date.now() - deviceTsRef.current > MIRROR_STALE_MS) event.currentTarget.pause();
          }}
          onPlaying={(event) => {
            if (!playing || !device.online || device.hidden || degraded || Date.now() - deviceTsRef.current > MIRROR_STALE_MS) event.currentTarget.pause();
          }}
          // 멈춰야 하는데 계속 흐르면 다시 멈춘다 — WebKit 은 배속을 바꾸는 중에 들어온 pause() 를 놓칠 때가 있다(2026-10-15 시험).
          onTimeUpdate={(event) => {
            const v = event.currentTarget;
            if (!v.paused && (!playing || !device.online || device.hidden || degraded || Date.now() - deviceTsRef.current > MIRROR_STALE_MS)) v.pause();
          }}
          onCanPlay={() => { const v = vref.current; if (v && !waitRef.current && playing && device.online && !device.hidden && Date.now() - deviceTsRef.current < MIRROR_STALE_MS) v.play().catch(() => {}); }}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain",
            opacity: hasFrame && !degraded ? 1 : 0, transition: "opacity .2s ease", // 로드/저속폴백 중엔 숨겨 썸네일 노출
          }} />
      )}
      {/* 학생 화면과 같은 위 자리(caption-bar.css .cap--top — 왼쪽 위 LIVE 배지를 비켜 간다) */}
      {captions && !isYt && <CaptionOverlay videoRef={vref} cues={captionCues} locale={captionLocale(captionLang)} enabled={hasFrame && !degraded} place="top" />}
      <div style={{ position: "absolute", top: 12, left: 12, display: "flex", gap: 6, alignItems: "center",
        background: "rgba(0,0,0,.5)", backdropFilter: "blur(8px)", padding: "4px 10px", borderRadius: 999 }}>
        <span className="badge-dot" style={{ width: 6, height: 6, background: device.online ? "var(--belt)" : "var(--text-3)", boxShadow: device.online ? "0 0 0 3px rgba(230,74,69,.3)" : "none" }} />
        <span style={{ fontSize: 10.5, fontWeight: 700, color: "#fff", letterSpacing: ".04em" }}>{device.online ? t("live") : t("offline")}</span>
      </div>
      {/* 유튜브 — 라이브 미러 대신 썸네일 상태뷰(옵션 가). 미러 고장 오해 방지용 뱃지·안내 */}
      {isYt && (
        <div style={{ position: "absolute", top: 12, right: 12, background: "rgba(0,0,0,.5)", backdropFilter: "blur(8px)", padding: "4px 10px", borderRadius: 999 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: "#fff", letterSpacing: ".04em" }}>{t("youtube")}</span>
        </div>
      )}
      {!playing && device.online && !mirrorPending && (
        // 종료/일시정지 시 재생 버튼 — 트랜스포트 재생과 같은 디자인 언어(accent 블루 그라데이션 + 소프트 섀도·inset 하이라이트)로 통일
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(10,20,45,.2)" }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "linear-gradient(180deg,#4a68d0,var(--accent))",
            boxShadow: "var(--shadow-accent)",
            display: "grid", placeItems: "center",
          }}>
            <I.play size={26} style={{ color: "#fff", marginLeft: 3 }} />
          </div>
        </div>
      )}
      {/* 저속 자동 전환 — 썸네일만 표시, 탭하면 스트리밍 재시도 */}
      {degraded && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(0,0,0,.45)" }}>
          <div className="col gap-8" style={{ alignItems: "center", textAlign: "center", padding: "0 16px" }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#fff" }}>{t("playOkTitle")}</span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,.65)" }}>{t("playOkDesc")}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                failCountRef.current = 0; // 수동 재시도 — 백오프 초기화(즉시 깨끗한 기회)
                if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
                diag("mirror", "미리보기 다시 시도 (수동)");
                setDegraded(false);
              }}
              style={{ marginTop: 2, padding: "6px 16px", borderRadius: 999, border: "1px solid rgba(255,255,255,.3)", background: "rgba(0,0,0,.5)", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
            >{t("retry")}</button>
          </div>
        </div>
      )}
      {mirrorPending && device.online && !degraded && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(0,0,0,.42)" }}>
          <div className="col gap-8" style={{ alignItems: "center", textAlign: "center", padding: "0 16px" }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#fff" }}>{t("previewPrepTitle")}</span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,.65)" }}>{t("previewPrepDesc")}</span>
          </div>
        </div>
      )}
      {!device.online && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(7,9,16,.72)" }}>
          <div className="col gap-8" style={{ alignItems: "center", color: "var(--text-2)" }}>
            <I.wifiOff size={30} /><span style={{ fontSize: 13, fontWeight: 600 }}>{t("disconnected")}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* 영상별 반복 횟수 선택지 — 제목 옆 xN 알약 메뉴 (1~10) */
const VIDEO_REPEAT_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

const QUEUE_GAP = 7; // .col gap-7 과 동일해야 드래그 계산이 맞음

function Queue({ np, device, onCmd, onReorder, onAddVideos }: {
  np: NowPlaying | null; device: DeviceView; onCmd: UICmd; onReorder: (items: string[]) => void | Promise<void>;
  onAddVideos?: () => void; // 내 재생목록일 때만 — 헤더의 '영상 추가' 알약 (상세 편집 화면으로 이동)
}) {
  const { byId } = useData();
  const locale = useLocale();
  const t = useTranslations("control.screen");
  // 드롭 즉시 새 순서를 그리는 낙관적 상태 — 디바이스가 새 순서를 보고(state:update)하면 해제,
  // 보고가 안 오면(오프라인·실패) 타임아웃 후 원래 순서로 복귀
  const [pending, setPending] = useState<string[] | null>(null);
  const live = np?.pl.items;
  // 디바이스가 새 순서를 보고하면 해제 — render 중 상태 조정 패턴(effect 금지 룰 회피)
  if (pending && live && pending.join("\n") === live.join("\n")) setPending(null);
  const items = pending ?? live ?? [];
  useEffect(() => {
    if (!pending) return;
    const t = setTimeout(() => setPending(null), 4000);
    return () => clearTimeout(t);
  }, [pending]);
  // 재생 중 강조: 낙관적 재정렬(pending) 중엔 device.index가 아직 옛 위치(디바이스 보고 전)라
  // videoId로 새 위치를 찾아 칠한다. 평상시엔 device.index가 정확(중복 영상까지 구분).
  const curIdx = pending ? items.findIndex((id) => id === device.videoId) : device.index;
  // 재생 중인 영상 자체만 큐에서 못 잡게(그립 숨김+드래그 차단) — 다른 영상은 그 자리를 지나
  // 자유롭게 재정렬되고, 재생 영상은 새 위치로 따라간다(디바이스가 videoId로 재앵커, 재생 안 끊김).
  const lockedIdx = device.status !== "idle" ? device.index : null;
  const grip = useGripReorder(items.length, QUEUE_GAP, (from, to) => {
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPending(next);
    onReorder(next);
  }, lockedIdx);
  if (!np) return null;
  // 영상별 반복 횟수 반영한 실제 재생 시간
  const total = items.reduce((a, id) => {
    // 유튜브 등 추가 시 길이 미저장(null) 항목 — 재생 중이면 기기가 보고한 라이브 길이로 총합에 반영(저장은 별도로 됨)
    let d = byId[id]?.durationSec || 0;
    if (!d && id === device.videoId && device.durationSec) d = device.durationSec;
    return a + d * (device.repeats?.[id] ?? 1);
  }, 0);

  return (
    <div className="col gap-10">
      {/* 재생목록 헤더 — 폰·태블릿 공통: 현재 재생목록 이름 우선(강조) + 우측 액션(영상추가·전체반복) */}
      <div className="ctl-qhead">
        <div className="col gap-2" style={{ minWidth: 0, flex: 1 }}>
          <span className="sec-title">{t("playlist")}</span>
          {/* 마스터 세트(마디별·품·낱기술)는 이름이 코드로 조립된 라벨("태극 1장 · 정면 마디")이라 tkdLabel 로 — 일반 재생목록은 그 안에서 tkdPlaylist 로 떨어진다 */}
          <div className="ctl-qhead__name">{tkdLabel(np.pl.name, locale)}</div>
          <span className="muted row" style={{ fontSize: 12, gap: 5, alignItems: "center" }}>
            {t("videoCount", { count: items.length })}
            <span style={{ opacity: .5 }}>·</span>
            <I.clock size={13} style={{ color: "var(--text-2)" }} />
            <span className="tnum" style={{ color: "var(--text-1)" }}>{fmt(total)}</span>
          </span>
        </div>
        <div className="row gap-7" style={{ flex: "none", alignItems: "center" }}>
          {/* 기본/품새도 다른 재생목록과 동일하게 '영상 추가'(도장별 오버라이드로 내 영상 덧붙임) */}
          {onAddVideos && (
            <button className="ctl-miniact" onClick={onAddVideos}>
              <I.plus size={14} sw={2.4} />{t("addVideos")}
            </button>
          )}
          <button
            className={"ctl-miniact" + (device.repeat ? " ctl-miniact--on" : "")}
            disabled={!device.online}
            onClick={() => onCmd("SET_REPEAT", device.repeat ? 0 : 1)}
            aria-label={device.repeat ? t("repeatAllOff") : t("repeatAllOn")}
          >
            <I.repeat size={13} sw={2.2} />{t("repeat")}
          </button>
        </div>
      </div>
      <div className="col gap-7">
        {items.map((id, i) => {
          const v = byId[id];
          if (!v) return null;
          const cur = i === curIdx;
          return (
            <button key={id} data-dragrow onPointerDown={grip.onRowDown(i)} onClick={() => { if (!grip.dragging) onCmd("JUMP", i); }} disabled={!device.online} className="row gap-10" style={{
              textAlign: "left", width: "100%", position: "relative",
              background: cur ? "rgba(58,91,200,.06)" : "transparent",
              border: "none", borderRadius: "var(--r-md)",
              padding: "9px 8px 9px 13px", cursor: device.online ? "pointer" : "default", opacity: device.online ? 1 : 0.6,
              ...grip.rowStyle(i),
            }}>
              {cur && <span aria-hidden style={{ position: "absolute", left: 0, top: 9, bottom: 9, width: 3, borderRadius: 999, background: "var(--accent)" }} />}
              <span className="mono" style={{ width: 16, textAlign: "center", fontSize: 12.5, fontWeight: 700, color: cur ? "var(--accent)" : "var(--text-3)" }}>
                {cur && device.status === "playing" ? <I.signal size={13} style={{ color: "var(--accent)" }} /> : i + 1}
              </span>
              <Poster video={v} w={44} h={32} showLabel={false} showDur={false} radius={7} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: cur ? 700 : 500, color: cur ? "var(--accent)" : "var(--text-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tkdTitle(v.title, locale)}</span>
              <span className="tnum" style={{ fontSize: 12.5, flex: "none", color: cur ? "var(--accent)" : "var(--text-3)", fontWeight: cur ? 700 : 500 }}>{fmt(v.durationSec)}</span>
              {/* 영상별 반복 횟수 — 표시 전용(설정은 위 컨트롤의 xN 알약), 기본(x1)이면 숨김 */}
              {(device.repeats?.[id] ?? 1) > 1 && (
                <span className="mono" style={{
                  flex: "none", padding: "4px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, lineHeight: 1,
                  background: "var(--accent-soft)", border: "1px solid var(--accent-line)", color: "var(--accent)",
                }}>
                  x{device.repeats?.[id] ?? 1}
                </span>
              )}
              {/* 정렬 핸들 — 재생 중인 영상은 고정이라 숨김 */}
              {device.online && !cur && <GripHandle onPointerDown={grip.onGripDown(i)} dragging={grip.dragging} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* 배속 + A-B 구간반복 원격 제어 (플레이어 로컬 상태와 동기화) */
const RATE_OPTIONS = [
  { value: "0.25", label: "0.25x" },
  { value: "0.5", label: "0.5x" },
  { value: "0.75", label: "0.75x" },
  { value: "1", label: "1.0x" },
  { value: "1.25", label: "1.25x" },
  { value: "1.5", label: "1.5x" },
  { value: "1.75", label: "1.75x" },
  { value: "2", label: "2.0x" },
];

/* 배속 알약 — 탭하면 위로 옵션 메뉴 (유튜브 패턴) */
function RatePill({ device, onCmd }: { device: DeviceView; onCmd: UICmd }) {
  const t = useTranslations("control.screen");
  const [open, setOpen] = useState(false);
  const disabled = !device.online;
  return (
    // data-tour — 사용법 둘러보기 '재생 조절' 칸이 배속을 따로 짚는다.
    <div data-tour="knob-rate" style={{ position: "relative" }}>
      <button
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="btn"
        style={{
          background: "none", border: "none", cursor: "pointer", padding: "3px 8px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 2, fontFamily: "var(--sans)",
          color: device.rate !== 1 ? "var(--accent)" : "var(--text-1)",
        }}
      >
        <span style={{ height: 18, display: "grid", placeItems: "center", fontSize: 14, fontWeight: 800, lineHeight: 1 }}>{device.rate === 1 ? "1.0×" : `${device.rate}×`}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: device.rate !== 1 ? "var(--accent)" : "var(--text-2)" }}>{t("speed")}</span>
      </button>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 30 }} onClick={() => setOpen(false)} />
          <div style={{
            position: "absolute", bottom: "calc(100% + 8px)", left: 0, zIndex: 31,
            background: "var(--surface-2)", border: "1px solid var(--border-strong)", borderRadius: "var(--r-md)",
            padding: 5, display: "flex", flexDirection: "column", gap: 2, boxShadow: "var(--shadow-2)",
          }}>
            {RATE_OPTIONS.map((o) => {
              const on = String(device.rate) === o.value;
              return (
                <button key={o.value}
                  // data-tour — 자동 시연 코스③이 '0.75배'를 실제로 골라 누른다. 값이 곧 표식이다.
                  data-tour={`rate-${o.value}`}
                  onClick={() => { onCmd("SET_RATE", Number(o.value)); setOpen(false); }}
                  style={{
                    background: on ? "var(--accent-soft)" : "transparent", border: "none", cursor: "pointer",
                    padding: "8px 16px", borderRadius: "var(--r-sm)", fontSize: 13, fontWeight: 700, textAlign: "left",
                    color: on ? "var(--accent)" : "var(--text-1)", fontFamily: "var(--sans)", whiteSpace: "nowrap",
                  }}>
                  {o.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* 현재 영상 반복 횟수 알약 — 탭하면 위로 옵션 메뉴 (배속 패턴). x1 = 기본(반복 없음) */
function VideoRepeatPill({ device, np, onSetRepeat }: {
  device: DeviceView; np: NowPlaying | null; onSetRepeat: (videoId: string, count: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const t = useTranslations("control.screen");
  const menuRef = useRef<HTMLDivElement>(null);
  // 메뉴를 열 때 현재 선택값이 보이게 가운데로 스크롤 (제어 화면이 자주 리렌더돼 effect 로 1회만)
  useEffect(() => {
    if (!open) return;
    const m = menuRef.current;
    const el = m?.querySelector("[data-sel]") as HTMLElement | null;
    if (m && el) m.scrollTop = el.offsetTop - m.clientHeight / 2 + el.clientHeight / 2;
  }, [open]);
  const vid = np?.vid;
  const rep = vid ? device.repeats?.[vid.id] ?? 1 : 1;
  const disabled = !device.online || !vid;
  const on = rep > 1;
  return (
    <div style={{ position: "relative" }}>
      <button
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-label={t("repeatCount", { n: rep })}
        style={{
          height: 34, padding: "0 14px", borderRadius: 999, cursor: "pointer",
          background: on ? "var(--accent-soft)" : "var(--surface-2)",
          border: "1px solid " + (on ? "var(--accent-line)" : "var(--border)"),
          color: on ? "var(--accent)" : "var(--text-2)",
          fontSize: 13, fontWeight: 700, fontFamily: "var(--sans)", opacity: disabled ? 0.5 : 1,
        }}
      >
        x{rep}
      </button>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 30 }} onClick={() => setOpen(false)} />
          {/* 1~10 세로 리스트 — 5개쯤 보이고 안에서 스크롤. 제목 옆(우측 끝) 배치라 right 기준, 아래로 펼침 */}
          <div ref={menuRef} style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 31,
            background: "var(--surface-2)", border: "1px solid var(--border-strong)", borderRadius: "var(--r-md)",
            padding: 5, display: "flex", flexDirection: "column", gap: 2, boxShadow: "var(--shadow-2)",
            maxHeight: 198, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain",
          }}>
            {VIDEO_REPEAT_OPTIONS.map((n) => {
              const sel = rep === n;
              return (
                <button key={n} data-sel={sel || undefined}
                  onClick={() => { if (vid) onSetRepeat(vid.id, n); setOpen(false); }}
                  style={{
                    background: sel ? "var(--accent-soft)" : "transparent", border: "none", cursor: "pointer",
                    padding: "9px 22px", borderRadius: "var(--r-sm)", fontSize: 13, fontWeight: 700, textAlign: "center", flex: "none",
                    color: sel ? "var(--accent)" : "var(--text-1)", fontFamily: "var(--sans)", whiteSpace: "nowrap",
                  }}>
                  x{n}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* A-B 반복 알약 — 어학기식 원버튼: 1탭 A 설정 → 2탭 B 설정+반복 → 3탭 해제 */
function ABPill({ device, position, onCmd }: {
  device: DeviceView; position: number; onCmd: UICmd;
}) {
  // A 지점(armed)은 '어느 영상에서 찍었는지'와 함께 든다. 다음/이전 영상으로 넘어가면 그 A는
  // 무의미해지는데, pendingA 는 컨트롤러 로컬이라 안 풀려 버튼이 'A 설정'으로 남던 증상이 있었다
  // (기기 쪽 loop 는 loadIndex 에서 이미 풀려 넘어옴). 영상 키를 같이 저장해 파생값으로 자동 무효화한다.
  const [pendingA, setPendingA] = useState<{ pos: number; vid: string } | null>(null);
  const t = useTranslations("control.screen");
  // A↔B 피드백은 전역 토스트 대신 이 버튼 바로 위 말풍선으로 잠깐 띄운다(조작 피드백은 누른 곳 근처가 자연스럽고 시선 이동↓).
  const [hint, setHint] = useState<{ msg: string; k: number } | null>(null);
  const hintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hintK = useRef(0);
  useEffect(() => () => { if (hintTimer.current) clearTimeout(hintTimer.current); }, []);
  const showHint = (msg: string) => {
    setHint({ msg, k: ++hintK.current }); // k 로 remount → 연속 조작 시 애니메이션 재시작
    if (hintTimer.current) clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => setHint(null), 2400);
  };
  const vidKey = `${device.videoId ?? ""}#${device.index}`;
  const armedPos = pendingA && pendingA.vid === vidKey ? pendingA.pos : null; // 다른 영상이면 무효(=idle)
  const disabled = !device.online;
  const state = device.loop ? "loop" : armedPos != null ? "armed" : "idle";
  const palette =
    state === "loop" ? { bg: "var(--warn-soft)", line: "rgba(255,159,10,.4)", fg: "var(--warn)" }
    : state === "armed" ? { bg: "var(--accent-soft)", line: "var(--accent-line)", fg: "var(--accent)" }
    : { bg: "var(--surface-2)", line: "var(--border)", fg: "var(--text-2)" };
  // 순환 화살표 색 — 대기:회색 / A설정:위 화살표 파랑 / 반복 진행중(구간지정):위·아래 둘 다 주황
  const arrowTop = state === "loop" ? "var(--warn)" : state === "armed" ? "var(--accent)" : "var(--text-3)";
  const arrowBot = state === "loop" ? "var(--warn)" : "var(--text-3)";
  const onClick = () => {
    if (disabled) return;
    if (device.loop) { onCmd("CLEAR_LOOP"); setPendingA(null); return; }
    if (armedPos == null) { setPendingA({ pos: position, vid: vidKey }); showHint(t("abHintStart")); return; }
    if (position <= armedPos) { showHint(t("abHintOrder")); return; }
    onCmd("SET_LOOP", `${armedPos.toFixed(1)},${position.toFixed(1)}`);
    setPendingA(null);
  };
  return (
    // 알약은 원래대로 자연폭(글자에 딱 붙음). ✕는 절대배치 배지라 폭을 안 먹고, 상태 문구도 'A-B'와 비슷한 폭이라 안 밀림.
    // data-tour — 사용법 둘러보기 '재생 조절' 칸이 구간반복을 따로 짚는다.
    <div data-tour="knob-ab" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      {hint && <div key={hint.k} className="ctl-ab-hint" role="status">{hint.msg}</div>}
      <button disabled={disabled} onClick={onClick}
        className="btn"
        style={{
          background: "none", border: "none", cursor: "pointer", padding: "3px 8px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
          color: palette.fg, fontFamily: "var(--sans)", whiteSpace: "nowrap",
        }}>
        {/* 배속·볼륨과 동일한 grid 센터링(place-items:center)로 통일 — inline-flex 였을 때 A·B가 ~3px 위로 떠 보이던 것 교정. gridAutoFlow:column 으로 A·화살표·B 가로 배치 유지 */}
        <span style={{ height: 18, display: "grid", gridAutoFlow: "column", placeItems: "center", gap: 2, fontSize: 14, fontWeight: 800, lineHeight: 1 }}>
          A
          {/* 순환 화살표 — 위/아래를 상태에 따라 단계적으로 채움(대기 회색 → A 위 파랑 → B 아래 주황) */}
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" style={{ display: "block", flex: "none" }}>
            <path d="M17 2l4 4-4 4M21 6H8a4 4 0 0 0-4 4v1" stroke={arrowTop} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7 22l-4-4 4-4M3 18h13a4 4 0 0 0 4-4v-1" stroke={arrowBot} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          B
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, color: palette.fg }}>{t("abLoop")}</span>
      </button>
      {(state === "armed" || state === "loop") && (
        <button onClick={(e) => { e.stopPropagation(); if (state === "loop") onCmd("CLEAR_LOOP"); else setPendingA(null); }}
          aria-label={state === "loop" ? t("abClear") : t("abCancel")}
          style={{
            position: "absolute", top: -7, left: -7, width: 20, height: 20, borderRadius: 999,
            background: "var(--surface-3)", border: "1px solid var(--border)", color: "var(--text-2)",
            display: "grid", placeItems: "center", fontSize: 10, lineHeight: 1, cursor: "pointer", zIndex: 1,
          }}>
          ✕
        </button>
      )}
    </div>
  );
}

// 품새 행 모드 메뉴(F안) — 우측 ⋯ 를 누르면 뜨는 애플 컨텍스트 메뉴풍 팝오버.
//  · 마디별/품/낱기술 = 동일 위계(muted+›) 드릴 진입. '전체' 재생은 행 몸통 탭이 하므로 메뉴엔 넣지 않는다(중복 제거).
//  · surface 는 backdrop-filter+overflow 라 그 안에서 절대배치하면 잘린다 → IdleBoard 최상위에 position:fixed 오버레이로 띄운다.
//  · 아이콘+제목만(설명 없음)·행 52px·구분선 아주 옅게. 데이터 있는 모드만 노출(유단자 품새는 마디별 1줄).
const DRILL_MODES = [
  { seg: "machi" as const, labelKey: "segMachi" as const, lead: <I.layers size={18} />, kind: "machi" },
  { seg: "poom" as const, labelKey: "segPoom" as const, lead: <I.grid size={18} />, kind: "poom" },
  { seg: "tech" as const, labelKey: "segTech" as const, lead: <I.list size={18} />, kind: "technique" },
];

function ModeItem({ primary, lead, label, trailing, onClick }: {
  primary?: boolean; lead: ReactNode; label: string; trailing: ReactNode; onClick: () => void;
}) {
  return (
    <button
      className={"ctl-modemenu-item" + (primary ? " ctl-modemenu-item--primary" : "")}
      role="menuitem"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", height: 52, padding: "0 11px", borderRadius: 9, border: "none", background: "transparent", cursor: "pointer", textAlign: "left" }}>
      <span style={{ width: 22, flex: "none", display: "grid", placeItems: "center", color: primary ? "var(--accent)" : "var(--text-2)" }}>{lead}</span>
      <span style={{ flex: 1, textAlign: "left", fontSize: 15.5, fontWeight: primary ? 700 : 600, letterSpacing: "-.01em", color: primary ? "var(--accent-ink)" : "var(--text-1)" }}>{label}</span>
      <span style={{ flex: "none", display: "grid", placeItems: "center", color: primary ? "var(--accent)" : "var(--text-3)" }}>{trailing}</span>
    </button>
  );
}

function PoomModeMenu({ form, rect, onDrill, onClose }: {
  form: MasterForm;
  rect: DOMRect;
  onDrill: (seg: "machi" | "poom" | "tech") => void;
  onClose: () => void;
}) {
  const ts = useTranslations("control.setlist"); // 모드 이름은 드릴 화면 세그먼트와 같은 말을 쓴다
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const modes = DRILL_MODES.filter((m) => form.videos.some((v) => parseSegment(v.segment).kind === m.kind));
  // 스크롤·ESC 로 닫힘. 바깥 클릭은 아래 스크림이 'click 을 소비'해 닫기만 하고 밑 행은 안 눌린다.
  useEffect(() => {
    const close = () => closeRef.current();
    const onScroll = () => close();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("scroll", onScroll, true); window.removeEventListener("keydown", onKey); };
  }, []);
  // ⋯ 오른쪽 끝에 맞춰 바로 아래(행을 안 가림). 아래 공간 없으면 위로, 좌우는 뷰포트 안으로 클램프.
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    const W = el.offsetWidth, H = el.offsetHeight, m = 8;
    const left = Math.min(Math.max(m, rect.right - W), window.innerWidth - W - m);
    let top = rect.bottom + 6;
    if (top + H > window.innerHeight - m) top = Math.max(m, rect.top - H - 6);
    setPos({ top, left });
  }, [rect]);
  return (
    <>
      {/* 스크림 = 화면 전체를 덮어 바깥 '클릭을 소비'(밑 행이 안 눌림). pointerdown 이 아니라 click 에 닫아
          제스처 끝까지 스크림이 떠 있게 → 클릭이 밑으로 새지 않는다. */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,.16)" }} />
      <div ref={ref} role="menu" onPointerDown={(e) => e.stopPropagation()} style={{
        position: "fixed", zIndex: 61, width: 190, padding: 5,
        top: pos?.top ?? rect.bottom + 6, left: pos?.left ?? rect.right - 190, visibility: pos ? "visible" : "hidden",
        borderRadius: "var(--r-md)", background: "var(--glass-solid)",
        backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)",
        border: "1px solid var(--glass-brd)", boxShadow: "var(--shadow-pop), inset 0 1px 0 var(--glass-hi)",
      }}>
        {modes.map((mo) => (
          <ModeItem key={mo.seg} lead={mo.lead} label={ts(mo.labelKey)} trailing={<I.chevR size={17} />} onClick={() => onDrill(mo.seg)} />
        ))}
      </div>
    </>
  );
}

/* 대기 화면 — Apple Music / Apple TV / visionOS 톤 프리미엄 글래스 라이브러리.
   하나의 글래스 Surface 위 행이 이어짐 · 거의 안 보이는 인셋 헤어라인 · 블러+얇은보더+옅은 elevation 으로 레이어감 ·
   16:9 소프트라운드 썸네일 + 큰 제목(품새명이 곧 급수 식별). 상단 '다시 틀기'=컴팩트 최근재생 글래스 카드, 검색은 탭 바로 아래(범위 명시).
   기본동작(관장 최다 사용)은 목록 맨 위 고정(지역별=드릴인). 행 탭=바로 캐스트(정면부터, 시점은 재생 중 큐로). 배경 오로라가 글래스 뒤에 은은히 비친다. */
function IdleBoard({
  recentCasts, onPickRecent, onPickPlay, onCreate, onLoadSheet,
  masterForms, onPlayMaster, onPlaySetMaster, onPlayMachiSetMaster,
}: {
  recentCasts?: RecentCast[];
  onPickRecent?: (r: RecentCast) => void;
  onPickPlay: (playlistId: string) => void;               // 재생목록/품새 전체 재생(정면부터)
  onPlayAt?: (playlistId: string, index: number) => void; // (미사용 — 시점버튼 폐기. 프롭 호환 유지)
  onCreate: () => void;
  onLoadSheet: () => void;                                 // 검색·전체 목록(로드시트)
  // 마스터 표준 품새 드릴(전체/마디별) — 있으면 품새 행 탭 시 바로 재생 대신 그 자리에서 뎁스.
  masterForms?: MasterForm[];
  onPlayMaster?: (v: MasterPlayVideo) => void;
  onPlaySetMaster?: (playlistId: string) => void;
  onPlayMachiSetMaster?: (videos: MasterPlayVideo[], title: string, startIndex?: number, practice?: DrillPlayOpt | null) => void;
}) {
  const { playlists, byId, plById } = useData();
  const t = useTranslations("control.screen");
  const [seg, setSeg] = useState<"default" | "mine">("default");
  const [inBasics, setInBasics] = useState(false);
  const [drillForm, setDrillForm] = useState<MasterForm | null>(null); // 품새 누르면 그 자리에서 전체/마디별 드릴
  const [drillSeg, setDrillSeg] = useState<"machi" | "poom" | "tech">("machi"); // 모드 메뉴에서 고른 진입 탭
  const [modeMenu, setModeMenu] = useState<{ pl: PlaylistView; form: MasterForm; rect: DOMRect } | null>(null); // ⋯ 팝오버 대상

  // 고정 [탭·검색]은 스크롤 밖 오버레이 → 본문의 상단 여백·마스크 길이를 헤더 실측 높이에 맞춘다(테마·글꼴 차이 자동 대응).
  // 콜백 ref = 드릴 진입/복귀로 노드가 붙었다 떨어져도 안전하게 재측정.
  const [headerH, setHeaderH] = useState(112);
  const roRef = useRef<ResizeObserver | null>(null);
  const headerRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    if (!el) return;
    const measure = () => setHeaderH(el.offsetHeight);
    measure();
    roRef.current = new ResizeObserver(measure);
    roRef.current.observe(el);
  }, []);

  const locale = useLocale();
  const segRef = useSegRefLabel();
  const basics = showsBasics(locale) ? playlists.filter((p) => p.isDefault && isBasicName(p.name)) : []; // 영문 UI = 기본동작 숨김
  const poomsae = [...playlists.filter((p) => p.isDefault && !isBasicName(p.name))].sort((a, b) => poomsaeRank(a.name) - poomsaeRank(b.name));
  const mine = playlists.filter((p) => !p.isDefault);
  const recents = (recentCasts ?? []).slice(0, 3);

  // 품새 → 대응하는 마스터 표준 품새 찾기(마디별 칩 노출/드릴용).
  // ⚠️ 도장 재생목록 id ≠ 마스터 도장 재생목록 id(도장별 시드라 별개)라 이름으로도 매칭해야 함.
  const formOf = (pl: PlaylistView): MasterForm | undefined =>
    onPlayMaster && onPlaySetMaster
      ? masterForms?.find((m) => m.playlistId === pl.id || m.name === pl.name)
      : undefined;
  // 드릴 진입 모드가 하나라도 있나(마디별·품·낱기술) → ⋯ 버튼 노출 여부. 품/낱기술은 허용 계정에만 내려온다.
  const hasDrill = (f?: MasterForm) =>
    !!f && f.videos.some((v) => { const k = parseSegment(v.segment).kind; return k === "machi" || k === "poom" || k === "technique"; });

  // 유리 표면 recipe — 반투명 배경 + 블러 + 얇은 보더 + 상단 하이라이트 + 옅은 elevation(오로라 위로 살짝 뜸)
  const GLASS = {
    background: "var(--glass-bg)",
    backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)",
    border: "1px solid var(--glass-brd)",
    boxShadow: "var(--glass-shadow), inset 0 1px 0 var(--glass-hi)",
  };

  const subText = (t: string): ReactNode => <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--text-2)" }}>{t}</span>;

  const labelOf = (pl: PlaylistView) => (pl.name.startsWith(BASIC_PREFIX) ? tkdBasicRegion(pl.name, locale) : tkdPlaylist(pl.name, locale));
  const coverOf = (pl: PlaylistView) =>
    byId[pl.items[0]] ? <Poster video={byId[pl.items[0]]} w="100%" h="100%" radius={0} showLabel={false} showDur={false} /> : <I.film size={20} />;
  // 최근 재생 카드 메타 — 영상 개수(재생목록/마스터셋). 단일 영상은 "영상 1개".
  const recentMeta = (r: RecentCast): string => {
    if (r.kind === "playlist") { const pl = playlists.find((p) => p.id === r.id); return pl ? t("videoCount", { count: pl.items.length }) : t("playlist"); }
    if (r.kind === "masterset") return t("videoCount", { count: r.videos.length });
    return t("oneVideo");
  };

  // Apple Music 행 — 16:9 소프트라운드 썸네일 + 큰 제목 + 보조(개수/설명). 배경 투명(유리가 비침), 카드박스 없음.
  const rowEl = (key: string, thumb: ReactNode, title: string, sub: ReactNode, onClick: () => void, nav?: boolean) => (
    <button key={key} onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 15, width: "100%", textAlign: "left",
      padding: "15px 16px", background: "transparent", border: "none", cursor: "pointer",
    }}>
      <div style={{ width: 66, height: 44, borderRadius: "var(--r-sm)", overflow: "hidden", flex: "none", background: "var(--surface-2)", boxShadow: "var(--shadow-1), inset 0 0 0 1px var(--glass-hi-1)", display: "grid", placeItems: "center", color: "var(--accent)" }}>{thumb}</div>
      <div className="col" style={{ flex: 1, minWidth: 0, gap: 5 }}>
        <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-.015em", color: "var(--text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
        {sub != null && <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>{sub}</div>}
      </div>
      {nav && <I.chevR size={18} style={{ color: "var(--text-3)", flex: "none" }} />}
    </button>
  );

  // 품새 행(A안) — 몸통 탭=전체 바로 재생(1탭), 우측 '마디' 칩=마디별 드릴.
  // 중첩 button 금지(hydration) → 형제 2버튼을 감싼 div. 마디 영상 없으면 칩 생략.
  // first=사용법 둘러보기가 가리킬 첫 품새 줄. 목록 전체를 밝히면 "여기를 누르세요"가 아니라
  // "이 화면 어딘가를 누르세요"가 되고, 목록이 화면보다 길면 말풍선이 화면 밖으로 밀린다(2026-08-05).
  const formRowEl = (pl: PlaylistView, first = false) => {
    const form = formOf(pl);
    return (
      <div key={pl.id} data-tour={first ? "pick-form" : undefined} style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <button onClick={() => onPickPlay(pl.id)} style={{
          display: "flex", alignItems: "center", gap: 15, flex: 1, minWidth: 0, textAlign: "left",
          padding: "15px 10px 15px 16px", background: "transparent", border: "none", cursor: "pointer",
        }}>
          <div style={{ width: 66, height: 44, borderRadius: "var(--r-sm)", overflow: "hidden", flex: "none", background: "var(--surface-2)", boxShadow: "var(--shadow-1), inset 0 0 0 1px var(--glass-hi-1)", display: "grid", placeItems: "center", color: "var(--accent)" }}>{coverOf(pl)}</div>
          <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-.015em", color: "var(--text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{labelOf(pl)}</div>
        </button>
        {hasDrill(form) && (
          // ⋯ 더보기(F안) — 마디 영상 여러 모드(마디별·품·낱기술)로 늘어 '마디별' 단일 칩을 승격(2026-07-16).
          //   누르면 컨텍스트 메뉴풍 팝오버(마디별·품·낱기술 드릴 진입). '전체' 재생은 몸통 탭이 하므로 메뉴엔 없다.
          //   크기는 옛 '마디별' 칩과 동일(36×30). toggle 재탭 닫힘 + pointerDown stopPropagation(바깥클릭-닫기와 안 싸우게).
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setModeMenu((cur) => (cur?.pl.id === pl.id ? null : { pl, form: form!, rect }));
              e.currentTarget.blur();
            }}
            aria-label={t("modeMenu", { name: labelOf(pl) })} aria-haspopup="menu" aria-expanded={modeMenu?.pl.id === pl.id}
            style={{
              flex: "none", marginRight: 12, width: 36, height: 30, borderRadius: 999, cursor: "pointer",
              display: "grid", placeItems: "center",
              border: "1px solid " + (modeMenu?.pl.id === pl.id ? "var(--accent-line)" : "var(--border)"),
              background: modeMenu?.pl.id === pl.id ? "var(--accent-soft)" : "transparent",
              color: modeMenu?.pl.id === pl.id ? "var(--accent-ink)" : "var(--text-2)",
            }}>
            <I.dots size={20} />
          </button>
        )}
      </div>
    );
  };

  // 하나의 글래스 Surface — 행 사이 거의 안 보이는 인셋 헤어라인(제목 시작점 정렬 = 표 느낌 제거)
  const surface = (items: ReactNode[]) => (
    // 스크롤은 바깥 컨테이너가 맡는다('다시 틀기'가 함께 밀려 올라가야 하므로) — 여기선 카드 모양만.
    <div className="col" style={{ flex: "none", borderRadius: "var(--r-lg)", ...GLASS }}>
      {items.map((el, i) => (
        <div key={i}>
          {el}
          {i < items.length - 1 && <div style={{ height: 1, background: "var(--border)", marginLeft: 97, opacity: 0.13 }} />}
        </div>
      ))}
    </div>
  );

  const items: ReactNode[] = [];
  if (seg === "default") {
    if (inBasics) {
      // 기본동작 지역 목록 (드릴인) — 맨 위 뒤로 행 + 지역별 캐스트 행
      items.push(rowEl("back", <I.back size={20} />, t("basics"), subText(t("pickRegion")), () => setInBasics(false)));
      basics.forEach((pl) => items.push(rowEl(pl.id, coverOf(pl), labelOf(pl), subText(t("videoCount", { count: pl.items.length })), () => onPickPlay(pl.id))));
    } else {
      // 기본동작 = 관장 최다 사용 → 목록 맨 위 고정. 지역 1개면 바로 캐스트, 여러 개면 드릴인.
      if (basics.length === 1) items.push(rowEl(basics[0].id, coverOf(basics[0]), t("basics"), subText(labelOf(basics[0])), () => onPickPlay(basics[0].id)));
      else if (basics.length > 1) items.push(rowEl("basics", <I.layers size={22} />, t("basics"), subText(t("byRegion", { count: basics.length })), () => setInBasics(true), true));
      poomsae.forEach((pl, idx) => items.push(formRowEl(pl, idx === 0)));
      if (poomsae.length === 0 && basics.length === 0) items.push(<div key="e" className="muted" style={{ padding: "36px 0", textAlign: "center", fontSize: 14 }}>{t("noForms")}</div>);
    }
  } else {
    items.push(rowEl("new", <I.plus size={22} />, t("newPlaylist"), subText(t("newPlaylistSub")), onCreate, true));
    if (mine.length === 0) items.push(<div key="e" className="muted" style={{ padding: "28px 0", textAlign: "center", fontSize: 14 }}>{t("noMine")}</div>);
    mine.forEach((pl) => items.push(rowEl(pl.id, coverOf(pl), labelOf(pl), subText(t("videoCount", { count: pl.items.length })), () => onPickPlay(pl.id))));
  }

  // '마디' 칩 → 마디별 드릴(FormDrill 자체 헤더에 뒤로 + 품새명). 전체는 행 몸통 탭에서 바로 재생하므로
  // 여기선 마디별로 바로 연다(안에서 전체 탭으로 되돌아갈 수도 있음).
  if (drillForm && onPlayMaster && onPlaySetMaster) {
    return (
      <div className="col" style={{ flex: 1, minHeight: 0, padding: "14px 16px 16px" }}>
        <FormDrill
          form={drillForm}
          onPlay={onPlayMaster}
          onPlaySet={onPlaySetMaster}
          onPlayMachiSet={onPlayMachiSetMaster}
          onBack={() => setDrillForm(null)}
          initialSeg={drillSeg}
          addedPlay={addedPlaySet(drillForm.playlistId, plById, byId)}
        />
      </div>
    );
  }

  // 상단 페이드 마스크 — 헤더 높이만큼 위쪽을 투명하게 지운다. 판을 덮는 게 아니라 카드 자신을 지우므로
  // 지워진 자리엔 진짜 배경(오로라)이 그대로 비쳐 '박스'가 생길 자리가 없다. 알약 사이 틈까지 깨끗.
  // 완전 투명 구간을 헤더 대부분까지 유지하다 검색바 하단(가려지는 구간)에서만 급히 불투명 → 밑의 카드는 또렷하게 등장.
  const fadeMask = `linear-gradient(to bottom, transparent 0, transparent ${Math.max(0, headerH - 22)}px, #000 ${Math.max(1, headerH - 4)}px)`;
  return (
    // 스크롤 본문(마스크) + 그 위 고정 [탭·검색] 오버레이. '다시 틀기'는 본문 첫 항목이라 스크롤하면 헤더 밑으로 사라진다.
    <div className="col" style={{ position: "relative", flex: 1, minHeight: 0 }}>
      <div className="col" style={{
        flex: 1, minHeight: 0, overflowY: "auto", padding: `${headerH}px 16px 16px`, gap: 14,
        // ⚠️ -webkit- 을 표준보다 먼저 — 순서가 바뀌면 Lightning CSS 가 표준을 지운다.
        WebkitMaskImage: fadeMask, maskImage: fadeMask,
      }}>
      {/* 다시 틀기 — 컴팩트 '최근 재생' 글래스 카드(버튼 느낌 제거·정보밀도 유지: 제목 + 개수 메타 + 재생 칩). 썸네일 없이 유리+미세 elevation. */}
      {recents.length > 0 && onPickRecent && (
        <div className="col" style={{ flex: "none", gap: 9 }}>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: "var(--text-3)", letterSpacing: ".02em" }}>{t("replay")}</span>
          <div className="row" style={{ gap: 9 }}>
            {recents.map((r, i) => (
              <button key={i} onClick={() => onPickRecent(r)} className="col" style={{
                flex: 1, minWidth: 0, alignItems: "flex-start", gap: 7, cursor: "pointer", textAlign: "left",
                padding: "11px 13px", borderRadius: "var(--r-md)",
                background: "var(--glass-bg)", backdropFilter: "var(--glass-blur)", WebkitBackdropFilter: "var(--glass-blur)",
                border: "1px solid var(--glass-brd)", boxShadow: "var(--glass-shadow-1), inset 0 1px 0 var(--glass-hi-1)",
              }}>
                <span style={{ fontSize: 13.5, fontWeight: 750, letterSpacing: "-.01em", color: "var(--text-1)", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{tkdLabel(r.label, locale)}</span>
                <span className="row" style={{ gap: 6, alignItems: "center", minWidth: 0, maxWidth: "100%" }}>
                  <span style={{ display: "grid", placeItems: "center", width: 17, height: 17, borderRadius: 999, background: "var(--accent)", color: "#fff", flex: "none" }}><I.play size={9} /></span>
                  <span style={{ fontSize: 11, fontWeight: 550, color: "var(--text-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{recentMeta(r)}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {surface(items)}
      </div>

      {/* [탭 · 검색] = 항상 맨 위 고정(스크롤 밖 오버레이). 본문 마스크 밖이라 늘 또렷한 '완전 불투명' 알약 2개.
          밑을 지나는 카드·틈은 본문 마스크가 이미 지워 아무것도 안 비친다(블러·불투명 판을 덮지 않으므로 박스도 없다).
          래퍼는 pointer-events:none(틈은 통과) · 알약만 auto. 높이는 headerRef 로 실측해 본문 여백·마스크에 반영. */}
      <div ref={headerRef} className="col" style={{
        position: "absolute", top: 0, left: 0, right: 0, zIndex: 3, gap: 10,
        padding: "14px 16px 6px", pointerEvents: "none",
      }}>
        {/* 세그먼트 — .seg 는 이미 불투명(--surface-2). 그림자로 목록 위에 얹힌 층을 만든다. */}
        <div style={{ pointerEvents: "auto", boxShadow: "var(--shadow-2)", borderRadius: "var(--r-md)" }}>
          <Segmented value={seg} onChange={(v) => { setSeg(v); setInBasics(false); }} options={[
            { value: "default", label: segRef },
            { value: "mine", label: t("myContent") },
          ]} />
        </div>

        {/* 검색 = 탭 바로 아래 서치바(기본동작·영상 전체 포함). 세그먼트와 같은 재질(완전 불투명 --surface-2). */}
        <button onClick={onLoadSheet} className="row" style={{
          flex: "none", width: "100%", gap: 9, alignItems: "center", cursor: "pointer", pointerEvents: "auto",
          padding: "11px 14px", borderRadius: "var(--r-md)", color: "var(--text-3)", fontWeight: 500, fontSize: 13.5,
          background: "var(--surface-2)", border: "1px solid var(--border)",
          boxShadow: "var(--shadow-2)",
        }}>
          <I.search size={16} style={{ color: "var(--text-3)", flex: "none" }} />
          <span>{t("searchPh")}</span>
        </button>
      </div>

      {/* ⋯ 모드 팝오버 — surface 바깥(최상위)에 position:fixed 로 띄워 클리핑 회피.
          전체=onPickPlay(도장 재생목록 1탭 재생) / 마디별·품·낱기술=그 탭으로 드릴 진입. */}
      {modeMenu && (
        <PoomModeMenu
          form={modeMenu.form}
          rect={modeMenu.rect}
          onDrill={(s) => { setDrillSeg(s); setDrillForm(modeMenu.form); setModeMenu(null); }}
          onClose={() => setModeMenu(null)}
        />
      )}
    </div>
  );
}

// 재생목록의 "반복재생 포함 남은 시간" — 현재 영상 이후 항목(각 반복횟수만큼) + 현재 영상의
// 남은 반복 + 이번 회차 잔여(durationSec - position). 전체 반복(repeat)이 켜져 있어도 "한 바퀴" 기준.
function remainingPlaylistSec(np: NowPlaying, device: DeviceView, position: number, byId: Record<string, VideoView>): number {
  const items = np.pl.items;
  if (items.length === 0) return 0;
  const idx = Math.max(0, Math.min(np.index, items.length - 1));
  const dur = (id: string) => byId[id]?.durationSec || 0;
  const reps = (id: string) => Math.max(1, device.repeats?.[id] ?? 1);
  // 현재 영상 이후 항목 — 각자 반복 횟수만큼 통째로 남음
  let rest = 0;
  for (let i = idx + 1; i < items.length; i++) rest += dur(items[i]) * reps(items[i]);
  // 현재 영상 — 아직 안 한 반복분 + 이번 회차 잔여
  const curId = items[idx];
  // 현재 영상 길이는 카탈로그(byId)가 0/누락이면(신규 업로드·마스터/레퍼런스 영상 등) 플레이어가
  // 보고한 실제 길이(device.durationSec)로 폴백 — 시크바(dur)와 같은 기준. 폴백이 없으면 curDur=0 이 되어
  // 현재 영상 잔여가 늘 0 → 그 영상 재생 내내 남은시간이 멈추고 경과만 흐르는 증상.
  const curDur = dur(curId) || device.durationSec || 0;
  const curReps = reps(curId);
  const played = Math.min(Math.max(0, device.repeatPlayed ?? 0), curReps - 1); // 완료 회차(현재 회차 제외)
  const curRemain = (curReps - 1 - played) * curDur + Math.max(0, curDur - position);
  return Math.max(0, rest + curRemain);
}

/* ============ THE CONTROL SCREEN ============ */
export function ControlScreen({
  device, position, captions = false, captionLang, sessionSec, cheer, onCheerDone, onCmd, onClose, onToast, onLoadSheet, onMenu, onReorder, onSetRepeat, onCreate, onAddVideos, wide = false,
  recentCasts, onPickRecent, onPickPlay, onPlayAt, onLaunchPlayerApp, remoteLaunchable, remoteLaunchNote, onCamera,
  masterForms, onPlayMaster, onPlaySetMaster, onPlayMachiSetMaster, allowBig = false,
}: {
  device: DeviceView;
  allowBig?: boolean; // 미러 '크게 보기'(유튜브 전체 화면식) 단추 — 🚧 우선 테스트 계정만(lib/flags appFullscreenAllowed)
  captions?: boolean; // 도장 설정 '동작 이름 자막' — 미러 위에도 같은 자막을 얹는다
  captionLang?: string; // 자막 언어 "en" | "de"
  wide?: boolean; // 가로 태블릿 2단 — true면 시네마 히어로(미러+조작 | 큐 사이드바). 폰 세로는 기존 단일 컬럼 유지.
  position: number; // interpolated seconds
  sessionSec?: number; // 진행 중인 수련 세션 경과초 — 헤더에 라이브 타이머(없으면 미표시)
  cheer?: { kind: string; id: number } | null; // 응원 발사 시 미러 위에 같은 이펙트
  onCheerDone?: () => void;
  onCmd: UICmd;
  onClose: () => void;
  onToast: (m: string) => void;
  onLoadSheet: () => void;
  onMenu: () => void;
  onReorder: (items: string[]) => void | Promise<void>;
  onSetRepeat: (videoId: string, count: number) => void;
  onCreate: () => void;
  onAddVideos: () => void; // 현재 재생 중인 내 재생목록에 영상 추가 (상세 편집 화면으로 이동)
  // 대기(idle) 보드용 — 미제공 시 idle 캐스트는 onCmd 폴백, 최근 레일 미노출
  recentCasts?: RecentCast[];
  onPickRecent?: (r: RecentCast) => void;
  onPickPlay?: (playlistId: string) => void;
  onPlayAt?: (playlistId: string, index: number) => void; // 시점별 시작 위치 캐스트
  onLaunchPlayerApp?: () => void;
  remoteLaunchable?: boolean; // false = 앱이 없는 플랫폼(아이폰·PC 등) → 버튼 대신 안내
  remoteLaunchNote?: string; // 안내 문구(lib/device-platform 의 remoteLaunchCopy 가 결정) — 버튼 대신, 또는 버튼 아래 보조줄
  onCamera?: () => void; // 카메라 시트 열기 — 실시간 보기 / 내 카메라 전송 중 선택(둘은 성격이 달라 분리 유지)
  // 대기 보드 품새 드릴(전체/마디별) — 마스터 표준 품새 재생 콜백(선택 기기 대상)
  masterForms?: MasterForm[];
  onPlayMaster?: (v: MasterPlayVideo) => void;
  onPlaySetMaster?: (playlistId: string) => void;
  onPlayMachiSetMaster?: (videos: MasterPlayVideo[], title: string, startIndex?: number, practice?: DrillPlayOpt | null) => void;
}) {
  const { plById, byId } = useData();
  const [volOpen, setVolOpen] = useState(false); // 하단 도크 볼륨 — 저빈도라 접이식(기본 접힘, 아이콘 1탭 시 인라인 슬라이더)
  const locale = useLocale();
  const t = useTranslations("control.screen");
  const tsl = useTranslations("control.setlist"); // 수련 모드 문구(드릴 스위치와 같은 말)
  const [dockOpen, setDockOpen] = useState(true); // 하단 도크 미니↔풀 — 큐를 넓게 볼 땐 접기(미니=재생·시크만 유지)
  // 하단 도크 실측 높이 — 스크롤 하단 패딩을 여기에 맞춰(+여유) 마지막 큐 항목이 도크에 안 가리게. 미니/풀/볼륨펼침/노치 자동 대응.
  const [dockH, setDockH] = useState(240);
  const dockRoRef = useRef<ResizeObserver | null>(null);
  const setDockNode = useCallback((node: HTMLDivElement | null) => {
    dockRoRef.current?.disconnect();
    if (!node) return;
    const measure = () => setDockH(node.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    dockRoRef.current = ro;
  }, []);
  // 태블릿(가로 2단) 상단 크롬(제목바 + 상태 줄) 실측 높이 — 이만큼을 각 패널 '안쪽' 여백으로 줘서
  // 본문이 유리 밑으로 지나가게 한다(폰의 sticky 헤더와 같은 플로팅). 상태 줄 유무·기기명 줄바꿈에 자동 대응.
  const [chromeH, setChromeH] = useState(0);
  const chromeRoRef = useRef<ResizeObserver | null>(null);
  const setChromeNode = useCallback((node: HTMLDivElement | null) => {
    chromeRoRef.current?.disconnect();
    if (!node) return;
    const measure = () => setChromeH(node.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    chromeRoRef.current = ro;
  }, []);
  // 태블릿 2단은 헤더가 스크롤 컨테이너 밖(.ctl-hd-float)이라 --hd-solid 를 화면 루트에 흘려보낸다
  // → 폰의 sticky 헤더와 똑같이 스크롤하면 상단바가 불투명해진다(왼쪽 기기목록 바와 짝이 맞는다).
  const setWideRoot = useSolidifyRoot();
  const np = computeNowPlaying(device, plById, byId);
  const dur = np?.vid?.durationSec || device.durationSec || 1;
  // 낙관 위치 — 드래그 직후 잠깐 **내가 겨눈 자리**를 보여준다.
  // 🛑 유튜브는 seek 가 재버퍼라 기기가 새 위치를 보고하기까지 몇 초가 걸린다. 그동안 화면에 쓰는
  //    position 은 기기의 **옛 보고에서 보간되어 계속 흘러서**, 노브가 제자리로 돌아가 거기서
  //    재생되는 것처럼 보인다("당기면 그리로 로딩이 안 되고 되돌아온다", 2026-08-31 신고).
  //    볼륨 슬라이더가 겪은 것과 같은 함정이다 — 위 VolumeSlider 주석 참고.
  // 🔑 놓는 조건이 볼륨과 다르다. 볼륨은 기기가 같은 값을 되보고하면 끝이지만, 위치는 **계속 흐르는
  //    값**이라 '같아지는' 순간이 없다. 그래서 **목표 근처에 닿으면**(=seek 가 실제로 먹었다) 놓고,
  //    안 닿으면 최대 대기까지만 붙잡는다(그 뒤엔 진짜 상태를 보여주는 게 맞다).
  const [seekPos, setSeekPos] = useState<number | null>(null);
  const seekHoldRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (seekHoldRef.current) clearTimeout(seekHoldRef.current); }, []);
  const scrubbingRef = useRef(false); // 재생바를 끄는 중 — 그동안은 손잡이를 놓지 않는다
  useEffect(() => {
    if (seekPos == null || scrubbingRef.current) return;
    if (Math.abs(position - seekPos) <= SEEK_LAND_TOL) setSeekPos(null); // 닿았다 — 손을 뗀다
  }, [position, seekPos]);
  const shownPos = seekPos ?? position;
  const pct = device.status === "idle" || !np ? 0 : (shownPos / dur) * 100;
  // 끄는 동안 — 손잡이만 그 자리에 붙잡아 둔다(기기엔 아직 안 보낸다). 손 떼면 아래 seek 가 한 번 보낸다.
  const scrub = (r: number) => {
    scrubbingRef.current = true;
    if (seekHoldRef.current) { clearTimeout(seekHoldRef.current); seekHoldRef.current = null; }
    setSeekPos(r * dur);
  };
  const seek = (r: number) => {
    scrubbingRef.current = false;
    const target = r * dur;
    setSeekPos(target);
    if (seekHoldRef.current) clearTimeout(seekHoldRef.current);
    seekHoldRef.current = setTimeout(() => setSeekPos(null), SEEK_HOLD_MS);
    onCmd("SEEK", target);
  };
  // ── 미러 크게 보기(2026-10-12 "유튜브 전체 화면마냥 — 그 안에서도 누르면 제어") ─────────────────────────
  // 미러를 화면 가득 키우고, 영상을 누르면 조작(이전·재생·다음·재생 위치)이 영상 위에 떴다가 3.5초 뒤 사라진다.
  // 🔑 미러를 다시 만들지 않는다 — 같은 자리의 껍데기(.ctl-big)만 화면 전체로 펴서 영상·맞춤 상태가 그대로 산다.
  // 전체 화면 API 가 있으면 그 껍데기를 전체 화면으로 올리고(주소창까지 사라짐·폰은 가로로 돌림),
  // 없으면(아이폰 사파리) 창을 가득 덮는 것까지만 한다. 시트·토스트는 전체 화면 밖이라 조작은 이 안에 있는 것만 쓴다.
  const [big, setBig] = useState(false);
  const [bigUi, setBigUi] = useState(true);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const bigUiTimer = useRef(0);
  const bigOn = big && !!np;
  const pokeBigUi = () => {
    setBigUi(true);
    window.clearTimeout(bigUiTimer.current);
    bigUiTimer.current = window.setTimeout(() => setBigUi(false), 3500);
  };
  useEffect(() => () => window.clearTimeout(bigUiTimer.current), []);
  const hasNp = !!np;
  useEffect(() => { if (!hasNp) setBig(false); }, [hasNp]); // 수업 종료·재생목록 비움 — 다음에 틀 때 큰 화면으로 튀어나오지 않게
  const enterBig = () => {
    setBig(true);
    pokeBigUi();
    const el = stageRef.current as (HTMLDivElement & { webkitRequestFullscreen?: () => void }) | null;
    const lock = () => { (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> })?.lock?.("landscape")?.catch(() => {}); };
    try {
      if (el?.requestFullscreen) el.requestFullscreen({ navigationUI: "hide" }).then(lock).catch(() => {});
      else el?.webkitRequestFullscreen?.();
    } catch { /* 전체 화면이 안 되면 창을 덮는 것으로 충분하다 */ }
  };
  const exitBig = () => {
    setBig(false);
    const doc = document as Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };
    if ((doc.fullscreenElement ?? doc.webkitFullscreenElement) !== stageRef.current) return;
    try { screen.orientation?.unlock?.(); } catch { /* noop */ }
    if (doc.exitFullscreen) doc.exitFullscreen().catch(() => {});
    else doc.webkitExitFullscreen?.();
  };
  // 브라우저가 전체 화면을 풀면(뒤로 가기·Esc·화면 회전으로 미러가 다시 만들어짐) 큰 화면도 같이 접는다.
  useEffect(() => {
    if (!big) return;
    const doc = document as Document & { webkitFullscreenElement?: Element | null };
    let entered = false;
    const sync = () => {
      if ((doc.fullscreenElement ?? doc.webkitFullscreenElement) === stageRef.current) entered = true;
      else if (entered) setBig(false);
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, [big]);
  // 재생목록 남은 시간(영상별 반복 포함) — 헤더에 수업 경과 타이머와 나란히 표시.
  const remainSec = np ? remainingPlaylistSec(np, device, position, byId) : null;
  // 경과·남은 타이머를 같은 박자로 똑딱이게 — 경과(sessionSec)의 초가 바뀌는 그 순간에만 남은시간 표시를 스냅샷.
  // 남은시간은 0.5초 보고+위치 보간이라 자체 위상/지터가 있어, 그대로 두면 경과와 다른 순간에 초가 넘어가
  // "흐름이 다르다"고 보였다. 경과 비트에 맞춰 샘플하면 둘이 같은 렌더(=같은 순간)에 넘어가고 지터도 흡수된다
  // (배속이면 한 비트에 -N 으로 정상 반영). 일시정지 땐 position 고정이라 같은 값이 다시 찍혀 멈춘 채 유지.
  const [remainShown, setRemainShown] = useState(remainSec);
  const [lastSession, setLastSession] = useState(sessionSec);
  if (sessionSec !== lastSession) {
    setLastSession(sessionSec);
    setRemainShown(remainSec);
  }

  // 수련 타이머: 수업 세션이 살아있는 동안 논스톱(재생·일시정지·정지 모두 흐른다).
  // 수업 종료(GO_HOME) 로 세션이 끝나면 sessionSec=null 이라 사라진다. (오프라인이면 숨김)
  const runClock = sessionSec != null && device.online;
  const showRemain = runClock && remainShown != null;
  // 타이머는 '얇은 유리 필' 두 개 — 내용 폭만큼만 차지해 헤더를 누르지 않는다(2026-07-14 확정).
  // 필을 나눈 이유: 수련은 세션 스코프(항상), 남음은 재생 스코프(재생 중에만)라 붙고 떨어지는 시점이 다르다.
  // 한 덩어리면 남음이 사라질 때 폭이 훅 줄어 눈에 띈다 — 따로 두면 수련 필이 자리를 지켜 흔들리지 않는다.
  // 수련은 라벨 없이 초록 점 하나로 '살아 있음'만(가장 자주 보는 값 = 설명이 필요 없다), 남음만 라벨을 단다.
  const timerNode = runClock ? (
    <>
      <span className="ctl-pill ctl-runclock" title={t("runClock")}>
        <span className="ctl-timer__dot" />
        <b>{fmt(sessionSec)}</b>
      </span>
      {showRemain && (
        <span className="ctl-pill ctl-remain" title={t("remainTitle")}>
          <I.hourglass size={12} />
          <span className="ctl-pill__label">{t("remain")}</span>
          <b>{fmt(remainShown)}</b>
        </span>
      )}
    </>
  ) : null;
  // 집 화면이면 상단바 부제로 밝힌다 — 도장에서 조작하는 관장이 '지금 보고 있는 게 남의 집 거실 화면'
  // 임을 알아야 카메라·수업 종료 같은 걸 무심코 누르지 않는다(그리드에선 그룹이 말해 주지만 여긴 단독 화면이다).
  const homeSub = device.atHome ? t("atHomeSub") : null;
  // 칭찬은 재생 조작과 성격이 달라 secondary 로 분리(v10). 파괴적 '수업 종료'는 응원·더보기와 세로 헤어라인+간격으로 구획해 오탭 방지(되돌리기 토스트는 cmd GO_HOME 에서).
  const rightActions = (
    <div className="row gap-6 ctl-hd-actions" style={{ alignItems: "center" }}>
      {/* 카메라 — 버튼 하나로 모으고 어떤 카메라인지는 시트에서 고른다.
          상단바 공간은 계속 쓰는 재생 제어에 내주고, 가끔 쓰는 카메라는 한 탭 더 들어가게(오탭으로 카메라가
          바로 켜지는 사고도 준다). '보기'와 '전송'은 방향·프라이버시·하울링이 전혀 달라 합치지 않는다. */}
      {device.online && onCamera && (
        <button data-tour="camera" className="iconbtn iconbtn--plain" onClick={onCamera} aria-label={t("camera")}><I.video size={23} sw={2.25} /></button>
      )}
      {/* data-tour="ctl-top" — 사용법 둘러보기 '수업 도구' 칸이 짚는 자리.
          🛑 카메라는 일부러 이 묶음 **밖**이다. 바로 다음 칸이 카메라 하나만 짚기 때문에,
             여기서 같이 밝히면 방금 본 걸 또 보는 꼴이 되고 문구(응원·잠금·종료)와도 안 맞는다.
          이 껍데기는 표식용일 뿐 — 바깥 줄과 같은 gap 이라 보기엔 달라지는 게 없다. */}
      <div data-tour="ctl-top" className="row gap-6" style={{ alignItems: "center" }}>
        {device.online && <CheerButton variant="icon" align="end" placement="down" onPick={(kind) => onCmd("CHEER", kind)} />}
        <button data-tour="hd-more" className="iconbtn iconbtn--plain" onClick={onMenu} aria-label={t("more")}><I.dots size={23} /></button>
        <span aria-hidden style={{ alignSelf: "center", width: 1, height: 20, background: "var(--border)", margin: "0 2px" }} />
        <button data-tour="hd-end" className="iconbtn iconbtn--plain" onClick={() => { onCmd("GO_HOME"); onClose(); }} aria-label={t("endClass")}
          style={{ color: "var(--danger)" }}><I.power size={23} sw={2.25} /></button>
      </div>
    </div>
  );
  // 헤더는 '어느 기기를 보고 있나(제목·교실) + 무엇을 할 수 있나(아이콘)'. 교실명은 기기의 정체라 제목에 딸린 서브줄로.
  // 흐르는 '상태'(수련·남음)만 헤더 밖 자기 줄로 내린다 — 동작과 성격이 달라 한 카드에 섞으면 리듬이 깨진다.
  const header = (
    <AppBar title={device.name} onBack={onClose} sub={homeSub ?? device.room ?? undefined} right={rightActions} />
  );
  // 헤더 바로 아래 상태 줄 — 줄 자체는 투명하고(유리 판때기 없음) 필만 떠 있다. 유리는 필이 입는다.
  // sticky 안에 두어 큐를 스크롤해도 타이머는 계속 보인다(기존 동작 유지).
  // 수련 모드 — 기기가 영상 끝에서 따라 하기를 기다리는 동안 남은 초(기기가 1초마다 보고). 기다릴 때만 붙는다.
  const practicePill = device.online && device.practiceWait ? (
    <span className="ctl-pill ctl-practice" title={tsl("practice")}>
      <span className="ctl-pill__label">{tsl("practiceLabel")}</span>
      <b>{tsl("practiceSec", { n: device.practiceWait.leftSec })}</b>
    </span>
  ) : null;
  const statusBar = timerNode || practicePill ? <div className="ctl-statusbar">{timerNode}{practicePill}</div> : null;

  // 미러 무대 — 세로(폰)·가로(태블릿) 두 배치가 같이 쓴다. cls=평소 껍데기 클래스(폰 .ctl-hero).
  const playing = device.status === "playing";
  const bigUiShown = bigUi || !playing; // 멈춰 있을 땐 조작을 계속 띄워 둔다
  const mirrorStage = (cls?: string) => (
    <div ref={stageRef} className={bigOn ? "ctl-big" : cls} style={bigOn ? undefined : { position: "relative" }}>
      <Mirror np={np} device={device} position={position} captions={captions} captionLang={captionLang}
        fill={bigOn} rounded={bigOn ? 0 : undefined}
        onToggle={bigOn
          ? () => { if (bigUiShown && playing) setBigUi(false); else pokeBigUi(); }
          : () => device.online && np && onCmd(playing ? "PAUSE" : "PLAY")} />
      {/* 응원 이펙트 — 미러(영상 미리보기) 박스 안에만, 둥근 모서리 따라 잘리게 */}
      {cheer && (
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: bigOn ? 0 : "var(--r-lg)", pointerEvents: "none" }}>
          <CheerOverlay key={cheer.id} kind={cheer.kind} onDone={onCheerDone ?? (() => {})} />
        </div>
      )}
      {allowBig && np && !bigOn && (
        <button className="ctl-big__enter" aria-label={t("bigEnter")} onClick={(e) => { e.stopPropagation(); enterBig(); }}><I.maximize size={17} sw={2.2} /></button>
      )}
      {bigOn && bigUiShown && np && (
        <div className="ctl-big__ui">
          <div className="ctl-big__mid">
            <button className="ctl-big__btn" aria-label={t("prev")} disabled={!device.online} onClick={() => { onCmd("PREV"); pokeBigUi(); }}><I.prev size={26} /></button>
            <button className="ctl-big__btn ctl-big__btn--play" aria-label={playing ? t("pause") : t("play")} disabled={!device.online}
              onClick={() => { onCmd(playing ? "PAUSE" : "PLAY"); pokeBigUi(); }}>
              {playing ? <I.pause size={34} /> : <I.play size={34} style={{ marginLeft: 3 }} />}
            </button>
            <button className="ctl-big__btn" aria-label={t("next")} disabled={!device.online} onClick={() => { onCmd("NEXT"); pokeBigUi(); }}><I.next size={26} /></button>
          </div>
          <div className="ctl-big__bot" onPointerDown={pokeBigUi}>
            <div className="ctl-big__title">{tkdTitle(np.vid?.title, locale)}<span>{t("nowPlayingIdx", { i: np.index + 1, n: np.pl.items.length })}</span></div>
            <div className="ctl-seekrow">
              <span className="mono ctl-seekrow__t">{fmt(shownPos)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SeekBar value={pct} onSeek={seek} onScrub={scrub} knob color="#fff"
                  loop={device.loop && dur ? { a: (device.loop.a / dur) * 100, b: (device.loop.b / dur) * 100 } : undefined} />
              </div>
              <span className="mono ctl-seekrow__t">{fmt(dur)}</span>
              <button className="ctl-big__exit" aria-label={t("bigExit")} onClick={exitBig}><I.minimize size={20} sw={2.2} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // 대기 중(온라인 + 재생목록 미적재) — 플레이어 메인과 같은 브라우즈(카드 고르기) 화면에서 바로 재생 시작.
  // 이 화면엔 수련/남음 타이머 서브줄을 띄우지 않는다(아직 재생 전 = 카드 고르는 단계). 교실명만 있는 원래 상단바.
  if (device.online && !device.playlistId) {
    return (
      <div className="screen col" style={{ height: "100%" }}>
        <AppBar title={device.name} onBack={onClose} sub={homeSub ?? device.room ?? undefined} right={rightActions} />
        <IdleBoard
          recentCasts={recentCasts} onPickRecent={onPickRecent}
          onPickPlay={onPickPlay ?? ((plId) => onCmd("LOAD_PLAYLIST", plId))}
          onPlayAt={onPlayAt ?? ((plId, index) => onCmd("LOAD_PLAYLIST_AT", `${plId},${index},1`))}
          onCreate={onCreate} onLoadSheet={onLoadSheet}
          masterForms={masterForms}
          onPlayMaster={onPlayMaster} onPlaySetMaster={onPlaySetMaster} onPlayMachiSetMaster={onPlayMachiSetMaster}
        />
      </div>
    );
  }

  // 오프라인 + 재생목록 미적재 — 조작할 게 없는 빈 조작부(죽은 시크바·비활성 버튼) 대신 상태 안내만.
  // 전원을 켜면 소켓 재연결로 device.online 이 바뀌며 이 화면이 자동으로 제어 화면으로 전환된다(수동 새로고침 불필요).
  // ⋯ 메뉴엔 오프라인에도 유효한 '이름 변경·등록 해제'가 있어 유지하고, 무의미한 '수업 종료' 버튼만 뺀다.
  if (!device.online && !np) {
    return (
      <div className="screen col" style={{ height: "100%" }}>
        <AppBar title={device.name} onBack={onClose} sub={homeSub ?? device.room}
          right={<button className="iconbtn iconbtn--plain" onClick={onMenu} aria-label={t("more")}><I.dots size={20} /></button>} />
        <div className="col" style={{ flex: 1, alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 32px", gap: 16 }}>
          <div style={{ width: 76, height: 76, borderRadius: 999, background: "var(--surface-2)", border: "1px solid var(--border)", display: "grid", placeItems: "center" }}>
            <I.wifiOff size={34} style={{ color: "var(--text-3)" }} sw={2} />
          </div>
          <div className="col gap-6" style={{ alignItems: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{t("deviceOff")}</div>
            <div className="muted" style={{ fontSize: 14, lineHeight: 1.6, maxWidth: 260 }}>
              {t.rich("deviceOffDesc", { br: () => <br /> })}
            </div>
          </div>
          {onLaunchPlayerApp &&
            (remoteLaunchable === false ? (
              // 앱이 없는 플랫폼(아이폰·아이패드·PC 등): 원격 실행 자체가 불가 → 버튼 대신 안내.
              // 문구는 플랫폼별로 다르다(remoteLaunchCopy) — 예전엔 PC 기기에도 "아이폰·아이패드…"가 떴다.
              <div className="muted" style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 260 }}>
                {remoteLaunchNote ?? t("remoteOther")}
              </div>
            ) : (
              <div className="col gap-8" style={{ alignItems: "center" }}>
                <button
                  className="btn"
                  onClick={onLaunchPlayerApp}
                  style={{
                    height: 46,
                    padding: "0 18px",
                    borderRadius: 999,
                    background: "var(--accent)",
                    color: "#fff",
                    border: "none",
                    fontWeight: 800,
                    boxShadow: "var(--shadow-accent)",
                    // 앱으로 등록한 기록이 없으면 눌러도 실패할 가능성이 높다 → 흐리게. 막지는 않음(플래그가 낡았을 수 있음).
                    opacity: remoteLaunchNote ? 0.5 : 1,
                  }}
                >
                  <I.power size={18} />
                  {t("launchApp")}
                </button>
                {remoteLaunchNote && (
                  <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.6, maxWidth: 260 }}>
                    {remoteLaunchNote}
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    );
  }

  // ── 가로 태블릿 2단 — 시네마 히어로(왼쪽 미러+조작 | 오른쪽 큐 사이드바). 폰 세로는 아래 기존 레이아웃 유지. ──
  if (wide) {
    return (
      <div className="screen col" ref={setWideRoot} style={{ height: "100%", position: "relative" }}>
        {/* 상단 크롬을 콘텐츠 '위에' 띄운다 — 폰과 같은 플로팅 유리. 아래 패널들은 --ctl-chrome-h 만큼
            안쪽 여백을 받아, 스크롤하면 본문이 유리 밑으로 지나간다(그래야 유리가 유리로 보인다). */}
        <div className="ctl-hd-float" ref={setChromeNode}>
          {header}
          {statusBar}
        </div>
        <div className="ctl-cinema" style={{ ["--ctl-chrome-h" as string]: `${chromeH}px` }}>
          <div className="ctl-cinema__main">
            {mirrorStage()}
            {np && (
              <div className="row gap-10" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <div className="col gap-2" style={{ minWidth: 0, flex: 1 }}>
                  <div data-tour="now-title" style={{ fontWeight: 800, fontSize: 20, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tkdTitle(np.vid?.title, locale)}</div>
                  <div className="muted" style={{ fontSize: 13 }}>{t("nowPlayingIdx", { i: np.index + 1, n: np.pl.items.length })}</div>
                </div>
                <div className="row gap-6" style={{ alignItems: "center", flex: "none" }}>
                  <VideoRepeatPill device={device} np={np} onSetRepeat={onSetRepeat} />
                </div>
              </div>
            )}
            {/* 진행바 — 영상 아래 인라인 시간 + 얇은 트랙(깔끔한 노브). A-B 노란 구간 포함. */}
            <div className="ctl-seekrow">
              <span className="mono ctl-seekrow__t">{fmt(shownPos)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SeekBar value={pct} onSeek={seek} onScrub={scrub} knob
                  loop={device.loop && dur ? { a: (device.loop.a / dur) * 100, b: (device.loop.b / dur) * 100 } : undefined} />
              </div>
              <span className="mono ctl-seekrow__t ctl-seekrow__t--dim">{fmt(dur)}</span>
            </div>
            {/* 태블릿 시네마 조작카드 — 넓은 폭 활용: 속도 · [이전 재생 다음] · 구간반복 한 줄 + 가로 볼륨 슬라이더 */}
            <div data-tour="ctl-knobs" className="ctl-card">
              <div className="ctl-cine-transport">
                {np && <RatePill device={device} onCmd={onCmd} />}
                <Transport device={device} onCmd={onCmd} big />
                {np && <ABPill device={device} position={position} onCmd={onCmd} />}
              </div>
              <VolumeSlider device={device} onCmd={onCmd} />
            </div>
          </div>
          <div className="ctl-cinema__side">
            <div className="ctl-cinema__scroll col">
              <Queue np={np} device={device} onCmd={onCmd} onReorder={onReorder} onAddVideos={onAddVideos} />
            </div>
            <button data-tour="load-playlist" className="ctl-loadbtn" onClick={onLoadSheet} aria-label={t("loadPlaylistBtn")}>
              <I.layers size={16} sw={2} />{t("loadPlaylistBtn")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen col" style={{ height: "100%", position: "relative" }}>
      {/* 2-B 완전 오버레이 플로팅 — 헤더를 스크롤 컨테이너 안 sticky 로 두어 본문이 유리 바 '밑으로' 지나가게.
          좌우 패딩은 스크롤 컨테이너에서 0 으로 내리고(유리 바 풀폭 정렬) 본문 col 안쪽에서만 패딩. */}
      <ScrollSolidify className="frame__scroll col" style={{ padding: 0 }}>
        <div className="ctl-hd-sticky">
          {header}
          {statusBar}
        </div>
        {/* 스크롤 = 미러 + 제목 + 큐. 최다행동은 아래 고정 도크로. 하단 패딩=도크 높이(접힘 시 축소해 큐 공간 확보) */}
        <div className="col gap-16" style={{ padding: `6px 18px ${dockH + 24}px` }}>
          {mirrorStage("ctl-hero")}
          {/* 지금 재생 중 — 제목 + 현재 영상 반복 */}
          {np && (
            <div className="row gap-10" style={{ justifyContent: "space-between", alignItems: "center" }}>
              <div className="col gap-6" style={{ minWidth: 0, flex: 1 }}>
                <div data-tour="now-title" style={{ fontWeight: 800, fontSize: 22, letterSpacing: "-0.02em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tkdTitle(np.vid?.title, locale)}</div>
                <div className="muted" style={{ fontSize: 13 }}>{t("nowPlayingIdx", { i: np.index + 1, n: np.pl.items.length })}</div>
              </div>
              <div className="row gap-6" style={{ alignItems: "center", flex: "none" }}>
                <VideoRepeatPill device={device} np={np} onSetRepeat={onSetRepeat} />
              </div>
            </div>
          )}
          <Queue np={np} device={device} onCmd={onCmd} onReorder={onReorder} onAddVideos={onAddVideos} />
        </div>
      </ScrollSolidify>
      {/* 하단 고정 도크(엄지존) — 그립 탭으로 미니(재생·시크만) ↔ 풀(+배속·구간반복·볼륨·재생목록) 토글 */}
      <div className="ctl-dock" ref={setDockNode}>
        <button className="ctl-dock__grip" onClick={() => setDockOpen((o) => !o)} aria-label={dockOpen ? t("dockCollapse") : t("dockExpand")}>
          <I.chevD size={18} style={{ transform: dockOpen ? "none" : "rotate(180deg)", transition: "transform .2s" }} />
        </button>
        {/* 시크 — 미니·풀 공통(재생 위치는 항상 손끝) */}
        <div className="ctl-seekrow">
          <span className="mono ctl-seekrow__t">{fmt(shownPos)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SeekBar value={pct} onSeek={seek} onScrub={scrub} knob
              loop={device.loop && dur ? { a: (device.loop.a / dur) * 100, b: (device.loop.b / dur) * 100 } : undefined} />
          </div>
          <span className="mono ctl-seekrow__t ctl-seekrow__t--dim">{fmt(dur)}</span>
        </div>
        {/* 트랜스포트 — 풀=대형, 미니=컴팩트. 재생/일시정지는 항상 유지 */}
        <Transport device={device} onCmd={onCmd} big={dockOpen} />
        {dockOpen && (
          <>
            <div data-tour="ctl-knobs" className="ctl-dock__sec">
              {np && <RatePill device={device} onCmd={onCmd} />}
              {np && <ABPill device={device} position={position} onCmd={onCmd} />}
              {/* 볼륨 — 저빈도라 접이식(아이콘 1탭 → 아래에 인라인 슬라이더 펼침).
                  애플 기기는 JS 로 미디어 볼륨을 못 바꿔(하드웨어 버튼 전용) 슬라이더가 무의미 → 바를 안 펼치고,
                  탭하면 음소거만 토글 + '음소거만 가능' 안내 토스트. */}
              {device.volumeFixed || isAppleDevice(device.platform) ? (
                <button data-tour="knob-vol" className={"ctl-miniact" + (device.muted ? " ctl-miniact--on" : "")} aria-label={device.muted ? t("unmute") : t("mute")}
                  onClick={() => {
                    onCmd("SET_VOLUME", device.muted ? 0.6 : 0); // 플레이어가 video.muted 토글(애플도 먹음)
                    onToast(t("appleMuteOnly"));
                  }}>
                  {device.muted ? <I.mute size={18} /> : <I.volume size={18} />}
                  {t("mute")}
                </button>
              ) : (
                <button data-tour="knob-vol" className={"ctl-miniact" + (volOpen ? " ctl-miniact--on" : "")} aria-label={t("volume")} onClick={() => setVolOpen((v) => !v)}>
                  {device.volume === 0 ? <I.mute size={18} /> : <I.volume size={18} />}
                  {device.volume === 0 ? t("mute") : `${Math.round(device.volume * 100)}`}
                </button>
              )}
            </div>
            {volOpen && !(device.volumeFixed || isAppleDevice(device.platform)) && <VolumeSlider device={device} onCmd={onCmd} />}
            {/* 재생목록 불러오기 = 보조 액션(메인은 재생 버튼) — 유리 서피스 + 얇은 보더 + 파란 텍스트/아이콘 */}
            <button data-tour="load-playlist" className="btn btn--block" onClick={onLoadSheet} style={{
              height: 48, gap: 7, fontSize: 15, fontWeight: 700,
              background: "color-mix(in srgb, var(--surface-2) 78%, transparent)",
              border: "1px solid var(--border)", color: "var(--accent-ink)",
            }}>
              <I.cast size={18} />{t("loadOtherPlaylist")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
